import { AuthSurface, AuthCard, AuthBrandHeader, AuthHeading } from './components/auth/AuthSurface';
import PlanCards from './components/plans/PlanCards';
import useSuitesProjectsLanguage from './components/useSuitesProjectsLanguage';
import { ProjectList, ProjectDialog } from './components/projects/ProjectViews';
import { rememberOverviewHistory } from './components/dashboard/useOverviewHistory';
import InvitationOnboarding from './components/team/InvitationOnboarding';
import TrialEntitlementSummary from './components/team/TrialEntitlementSummary';
import TeamManagementSection from './components/team/TeamManagementSection';
import OrganizationAccessNotice from './components/team/OrganizationAccessNotice';
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import { BACKEND_URL } from './utils/backendURL';
import { showSuccess, showError, showWarning, showInfo } from './utils/notifications';
import { LanguageProvider } from './contexts/LanguageContext';
import { 
  WorkspaceProvider, 
  useWorkspace, 
  WorkspaceNotFound, 
  WorkspaceSuspended, 
  WorkspaceLoading 
} from './contexts/WorkspaceContext';
import LandingPageV2 from './components/landing/LandingPageV2';
import GetStartedFunnel from './components/funnels/GetStartedFunnel';
import BookDemoFunnel from './components/funnels/BookDemoFunnel';
import TalkToHumanFunnel from './components/funnels/TalkToHumanFunnel';

// Debug mode - set to false to disable console.log messages (only errors/warnings will show)
const DEBUG_MODE = false;
const debugLog = (...args) => {
  if (DEBUG_MODE) console.log(...args);
};

// Import Google Analytics utilities
import {
  trackPageView,
  trackLogin,
  trackRegistration,
  trackAPITestCreation,
  trackAPITestExecution,
  setUserProperties,
  trackFeatureUsage
} from './utils/analytics';
import { Card } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './components/ui/dialog';
import { Alert, AlertDescription } from './components/ui/alert';
import { Toaster } from './components/ui/toaster';
import APITesting from './components/APITesting';
import FlowTesting from './components/FlowTesting';
import TestSuites from './components/TestSuites';
import SprintQA from './components/SprintQA';
import SprintQAProjects from './components/SprintQAProjects';
import SupportTicket from './components/SupportTicket';
import Support from './components/Support';
import Integrations from './components/Integrations';
import CICD from './components/CICD';
import BillingPayments from './components/BillingPayments';
import DemoRequestModal from './components/DemoRequestModal';
import LandingFooter from './components/LandingFooter';
import PartnerProgram from './components/PartnerProgram';

// Solution pages
import SolutionsIndex from './components/solutions/SolutionsIndex';
import WebTesting from './components/solutions/WebTesting';
import MobileTesting from './components/solutions/MobileTesting';
import VisualTesting from './components/solutions/VisualTesting';
import APITestingSolution from './components/solutions/APITesting';
import CrossBrowserTesting from './components/solutions/CrossBrowserTesting';
// AppFooter removed - using DashboardFooter in UserSidebarLayout instead
import SEO from './components/SEO';
import Documentation from './components/Documentation';
import AdminPanel from './components/AdminPanel';
import UserSidebarLayout from './components/UserSidebarLayout';
import UserSettings from './components/UserSettings';
import DashboardOverview from './components/dashboard/DashboardOverview';
import { initialSignupForm, signupContext } from './components/auth/signupPrefill';
import OPSDashboard from './components/OPSDashboard';
import Pricing from './components/Pricing';
import Contact from './components/Contact';
import About from './components/About';
import Status from './components/Status';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Security from './components/Security';
import Features from './components/Features';
import Changelog from './components/Changelog';
import Roadmap from './components/Roadmap';
import Guides from './components/Guides';
import APIReference from './components/APIReference';
import Careers from './components/Careers';
import Press from './components/Press';
import Blog from './components/Blog';
import { 
  PlayCircle, 
  PlusCircle, 
  Code, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Trash2,
  Brain,
  Zap,
  Target,
  BarChart3,
  RefreshCw,
  Pause,
  Play,
  Settings,
  Plug,

  User,
  LogOut,
  Crown,
  Users,
  CreditCard,
  AlertTriangle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  UserPlus,
  Mail,
  Shield,
  Edit,
  Trash,
  Server,
  Loader2,
  Image,
  ExternalLink,
  Send,
  X,
  Bell,
  CheckCircle2,
  Check,
  FolderKanban,
  Plus,
  GitBranch,
  Github,
  Bug,
  MessageSquare,
  Eye,
  EyeOff
} from 'lucide-react';
import Logo from './components/Logo';
import StripePayment from './components/StripePayment';
import OveragePurchase from './components/OveragePurchase';
import IDTooltip from './components/IDTooltip';
import './App.css';

// Cache busting to ensure users always see latest changes
const FRONTEND_VERSION = "3.1.4"; // Increment this when major changes are made
const getVersionedUrl = (url) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${FRONTEND_VERSION}&t=${Date.now()}`;
};

// Enhanced axios configuration for cache busting
axios.interceptors.request.use((config) => {
  // Add cache busting headers for API requests
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';
  
  // Add version parameter to ensure fresh data
  if (config.url && !config.url.includes('v=')) {
    config.url = getVersionedUrl(config.url);
  }
  
  return config;
});

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor for automatic token refresh on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying and not login/register request
    if (error.response?.status === 401 && 
        !originalRequest._retry && 
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/register') &&
        !originalRequest.url?.includes('/auth/refresh')) {
      
      if (isRefreshing) {
        // Queue this request to retry after refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        }).catch(err => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          const response = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          
          if (response.data.access_token) {
            const newToken = response.data.access_token;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('token_domain');
        delete axios.defaults.headers.common['Authorization'];
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Backend URL imported from utility at top

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Token refresh function
  const refreshToken = async () => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return false;
      
      const response = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        debugLog('🔄 Token refreshed successfully');
        return true;
      }
    } catch (error) {
      debugLog('⚠️ Token refresh failed:', error.message);
      return false;
    }
    return false;
  };

  // Set up automatic token refresh every 20 minutes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Refresh token every 20 minutes to keep session alive
    const refreshInterval = setInterval(async () => {
      await refreshToken();
    }, 20 * 60 * 1000); // 20 minutes
    
    return () => clearInterval(refreshInterval);
  }, [user]);

  // Handle cross-domain auth: Check for token in URL (from workspace redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');
    
    if (authToken) {
      // Store the token from URL
      localStorage.setItem('token', authToken);
      localStorage.setItem('token_domain', window.location.hostname);
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      
      // Clean up URL (remove token from address bar for security)
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      
      // Fetch user info with the new token
      fetchUserInfo();
      debugLog('✅ Cross-domain auth: Token received from redirect');
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedDomain = localStorage.getItem('token_domain');
    const currentDomain = window.location.hostname;
    
    // One-time migration: Clear old tokens that don't have domain tracking
    // This helps users transition from old system to new domain-aware system
    const migrationDone = sessionStorage.getItem('domain_migration_v1');
    if (token && !storedDomain && !migrationDone) {
      debugLog('🔄 Migrating to domain-aware token system, clearing old token');
      localStorage.removeItem('token');
      sessionStorage.setItem('domain_migration_v1', 'true');
      setLoading(false);
      return;
    }
    
    // Clear token if domain changed (e.g., preview → production)
    // This prevents login issues when deploying to different environments
    if (token && storedDomain && storedDomain !== currentDomain) {
      debugLog(`🔄 Domain changed from ${storedDomain} to ${currentDomain}, clearing old token`);
      localStorage.removeItem('token');
      localStorage.removeItem('token_domain');
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
      return;
    }
    
    if (token) {
      // Store current domain for future validation
      localStorage.setItem('token_domain', currentDomain);
      
      // Set default axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch user info
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(response.data);
      
      // Check if we should redirect logged-in user to their workspace
      const hostname = window.location.hostname;
      const isPreviewEnv = hostname.includes('emergentagent.com') || 
                           hostname.includes('emergent.host') ||
                           hostname === 'localhost' ||
                           hostname === '127.0.0.1';
      const isMainDomain = hostname === 'lexa.run' || 
                           hostname === 'www.lexa.run' || 
                           hostname === 'app.lexa.run';
      
      // Check if user just logged out (don't redirect them back)
      const urlParams = new URLSearchParams(window.location.search);
      const justLoggedOut = urlParams.get('logged_out') === 'true';
      
      if (justLoggedOut) {
        // Clear the logged_out param from URL and clear token (they logged out from workspace)
        localStorage.removeItem('token');
        localStorage.removeItem('token_domain');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        window.history.replaceState({}, document.title, window.location.pathname);
        debugLog('🚪 User logged out from workspace, staying on main domain');
        return;
      }
      
      // Only redirect if on main domain (not preview, not already on workspace)
      if (!isPreviewEnv && isMainDomain && response.data.organization_id) {
        // Fetch user's workspace to redirect
        try {
          const workspaceResponse = await axios.get(`${BACKEND_URL}/api/workspaces/my-workspace`);
          if (workspaceResponse.data && workspaceResponse.data.subdomain) {
            const token = localStorage.getItem('token');
            const workspaceUrl = `https://${workspaceResponse.data.subdomain}.lexa.run?auth_token=${encodeURIComponent(token)}`;
            debugLog('🔄 Redirecting logged-in user to workspace:', workspaceResponse.data.subdomain);
            window.location.href = workspaceUrl;
            return; // Don't set loading to false, we're redirecting
          }
        } catch (wsError) {
          // No workspace found, stay on main domain
          debugLog('ℹ️ No workspace found for user, staying on main domain');
        }
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      // Token might be invalid, remove it
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch latest user data to ensure UI shows current state (READ-ONLY)
  const fetchLatestUserData = async () => {
    try {
      debugLog('👤 Fetching latest user profile (read-only)...');
      
      // Clear any cached UI data that might interfere with new functionality
      sessionStorage.clear();
      
      // Fetch current user data from server (does not modify any data)
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userResponse = await axios.get(`${BACKEND_URL}/api/auth/me`);
          setUser(userResponse.data);
          debugLog('✅ Latest user profile fetched');
        } catch (error) {
          console.warn('Failed to fetch user profile:', error);
        }
      }
      
      debugLog('✅ Latest user data fetch completed');
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const login = async (email, password) => {
    try {
      // Clear any existing data before login
      localStorage.removeItem('token');
      sessionStorage.clear();
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      
      debugLog('🔐 Logging in user...');
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData, workspace } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('token_domain', window.location.hostname);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      // Track successful login
      trackLogin('email');
      setUserProperties(userData.id, {
        email: userData.email,
        role: userData.role,
        plan: userData.subscription?.plan || 'free'
      });
      
      // Fetch latest dashboard state to ensure user sees new functionality
      setTimeout(() => {
        fetchLatestUserData();
      }, 500);
      
      debugLog('✅ Login successful - all latest changes will be visible');
      
      // Return workspace info for redirect
      return { success: true, workspace };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (name, email, password, organization_name, subdomain = '') => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        name,
        email,
        password,
        organization_name,
        subdomain: subdomain || undefined  // Don't send empty string
      });
      
      const { access_token, user: userData, workspace } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('token_domain', window.location.hostname);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      // Track successful registration
      trackRegistration('email');
      
      // Return workspace info for redirect
      return { success: true, workspace };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = () => {
    try {
      debugLog('🚪 Logging out user...');
      
      // Clear all user-related data comprehensively
      localStorage.removeItem('token');
      localStorage.removeItem('token_domain');
      sessionStorage.clear();
      
      // Clear axios authorization header
      delete axios.defaults.headers.common['Authorization'];
      
      // Clear all user state
      setUser(null);
      
      // Check if we're on a workspace subdomain (not main domain or preview)
      const hostname = window.location.hostname;
      const isPreviewEnv = hostname.includes('emergentagent.com') || 
                           hostname.includes('emergent.host') ||
                           hostname === 'localhost' ||
                           hostname === '127.0.0.1';
      const isWorkspaceSubdomain = !isPreviewEnv && 
                                    hostname.endsWith('.lexa.run') && 
                                    hostname !== 'lexa.run' && 
                                    hostname !== 'www.lexa.run' &&
                                    hostname !== 'app.lexa.run';
      
      if (isWorkspaceSubdomain) {
        // Redirect to main domain on logout from workspace
        // Add logged_out flag to prevent auto-redirect back to workspace
        debugLog('✅ Logout completed - redirecting to main domain');
        window.location.href = 'https://lexa.run?logged_out=true';
      } else {
        // Normal logout - just refresh
        setTimeout(() => {
          debugLog('✅ Logout completed - page will refresh to clear all cached data');
          window.location.reload();
        }, 100);
      }
      
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, ensure we clear the state and redirect/refresh
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = 'https://lexa.run';
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      loading,
      isAuthenticated: !!user,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page Component
const LoginPage = ({ onBack, onSwitchToRegister, onForgotPassword }) => {
  const [formData, setFormData] = useState(() => {
    // Pre-fill email when arriving from a funnel via /?login=1&email=...
    try {
      const raw = sessionStorage.getItem('funnelLoginContext');
      if (raw) {
        const ctx = JSON.parse(raw);
        return { email: ctx.email || '', password: '' };
      }
    } catch (_) { /* ignore */ }
    return { email: '', password: '' };
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    // Friendly message if we came here because the email was already registered
    try {
      const raw = sessionStorage.getItem('funnelLoginContext');
      if (raw) {
        const ctx = JSON.parse(raw);
        sessionStorage.removeItem('funnelLoginContext');
        if (ctx.email) {
          return `Looks like you already have an account for ${ctx.email}. Please sign in.`;
        }
      }
    } catch (_) { /* ignore */ }
    return '';
  });
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);

    if (result.success) {
      // Check if we should redirect to workspace subdomain
      // Skip redirect in Emergent preview environment or localhost
      const isPreviewEnv = window.location.hostname.includes('emergentagent.com') || 
                           window.location.hostname.includes('emergent.host') ||
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';
      
      if (!isPreviewEnv && result.workspace && result.workspace.url) {
        // Get the token to pass to subdomain (cross-domain auth)
        const token = localStorage.getItem('token');
        // Redirect to workspace URL with token for seamless auth
        const redirectUrl = `${result.workspace.url}?auth_token=${encodeURIComponent(token)}`;
        window.location.href = redirectUrl;
      } else {
        // Fallback to normal flow (preview env or no workspace)
        onBack();
      }
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  return (
    <AuthSurface>
      <SEO 
        title="Sign In | Lexa.run"
        description="Sign in to your Lexa.run account to access AI-powered test automation tools and manage your testing workflows."
      />
      <div className="w-full max-w-md">
        <AuthCard>
          <AuthBrandHeader onBack={onBack} />

          <AuthHeading title="Welcome Back" description="Sign in to your account to continue" />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <Input
                id="login-email"
                autoComplete="username"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="Enter your email"
                className="h-12"
              />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  id="login-password"
                  autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Enter your password"
                  className="h-12 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#6d28d9] hover:bg-[#5b21b6] text-white font-medium"
            >
              {loading ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500 mb-4">
              Don't have an account?{' '}
              <button
                onClick={onSwitchToRegister}
                className="text-violet-700 hover:text-violet-800 font-medium"
              >
                Create Account
              </button>
            </p>
            <p className="text-slate-500">
              <button
                onClick={onForgotPassword}
                className="text-violet-700 hover:text-violet-800 font-medium"
              >
                Forgot your password?
              </button>
            </p>
          </div>
        </AuthCard>
      </div>
    </AuthSurface>
  );
};

// Forgot Password Page Component
const ForgotPasswordPage = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: email.trim()
      });

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthSurface>
        <SEO 
          title="Password Reset Sent | Lexa.run"
          description="Check your email for password reset instructions."
        />
        <div className="w-full max-w-md">
          <AuthCard>
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950 mb-4">Check Your Email</h2>
              <p className="text-slate-500 mb-6">
                If an account with email <strong>{email}</strong> exists, you will receive a password reset link within a few minutes.
              </p>
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Didn't receive an email? Check your spam folder or try again.
                </p>
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="w-full"
                >
                  Back to Sign In
                </Button>
              </div>
            </div>
          </AuthCard>
        </div>
      </AuthSurface>
    );
  }

  return (
    <AuthSurface>
      <SEO 
        title="Forgot Password | Lexa.run"
        description="Reset your password to regain access to your Lexa.run account."
      />
      <div className="w-full max-w-md">
        <AuthCard>
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-violet-50 rounded-2xl flex items-center justify-center mb-6">
              <Shield className="h-8 w-8 text-violet-700" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-950 mb-2">Forgot Password?</h2>
            <p className="text-slate-500">Enter your email address and we'll send you a link to reset your password.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="recovery-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="recovery-email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full rounded-full bg-[#6d28d9] hover:bg-[#5b21b6] text-white py-3"
            >
              {loading ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Sending Reset Link...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Reset Link
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500">
              Remember your password?{' '}
              <button
                onClick={onBack}
                className="text-violet-700 hover:text-violet-800 font-medium"
              >
                Back to Sign In
              </button>
            </p>
          </div>
        </AuthCard>
      </div>
    </AuthSurface>
  );
};

// Reset Password Page Component
const ResetPasswordPage = ({ token, onBack }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [validatingToken, setValidatingToken] = useState(true);

  // Verify token on component mount
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify-reset-token/${token}`);
      setTokenInfo(response.data);
    } catch (error) {
      console.error('Token verification error:', error);
      setError('Invalid or expired reset token. Please request a new password reset.');
    } finally {
      setValidatingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token: token,
        new_password: formData.password
      });

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <AuthSurface>
        <div className="text-center">
          <Clock className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-slate-500">Verifying reset token...</p>
        </div>
      </AuthSurface>
    );
  }

  if (success) {
    return (
      <AuthSurface>
        <SEO 
          title="Password Reset Successful | Lexa.run"
          description="Your password has been successfully reset."
        />
        <div className="w-full max-w-md">
          <AuthCard>
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950 mb-4">Password Reset Successful!</h2>
              <p className="text-slate-500 mb-6">
                Your password has been successfully updated. You can now sign in with your new password.
              </p>
              <Button
                onClick={onBack}
                className="w-full rounded-full bg-[#6d28d9] hover:bg-[#5b21b6] text-white"
              >
                Continue to Sign In
              </Button>
            </div>
          </AuthCard>
        </div>
      </AuthSurface>
    );
  }

  return (
    <AuthSurface>
      <SEO 
        title="Reset Password | Lexa.run"
        description="Create a new password for your Lexa.run account."
      />
      <div className="w-full max-w-md">
        <AuthCard>
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-violet-50 rounded-2xl flex items-center justify-center mb-6">
              <Shield className="h-8 w-8 text-violet-700" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-950 mb-2">Reset Your Password</h2>
            {tokenInfo && (
              <p className="text-slate-500">
                Creating new password for <strong>{tokenInfo.email}</strong>
              </p>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                id="reset-password"
                autoComplete="new-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Enter new password (min 8 characters)"
                required
                disabled={loading}
                minLength="8"
              />
            </div>

            <div>
              <label htmlFor="reset-confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                id="reset-confirm-password"
                autoComplete="new-password"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Confirm new password"
                required
                disabled={loading}
                minLength="8"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.password || !formData.confirmPassword}
              className="w-full rounded-full bg-[#6d28d9] hover:bg-[#5b21b6] text-white py-3"
            >
              {loading ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500">
              <button
                onClick={onBack}
                className="text-violet-700 hover:text-violet-800 font-medium"
              >
                Back to Sign In
              </button>
            </p>
          </div>
        </AuthCard>
      </div>
    </AuthSurface>
  );
};

// Register Page Component
const RegisterPage = ({ onBack, onSwitchToLogin }) => {
  const [formData, setFormData] = useState(initialSignupForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState({ checking: false, available: null, suggestion: null });
  const { register } = useAuth();

  // Check subdomain availability with debounce
  const checkSubdomainAvailability = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainStatus({ checking: false, available: null, suggestion: null });
      return;
    }
    
    setSubdomainStatus(prev => ({ ...prev, checking: true }));
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/workspaces/check-subdomain/${subdomain.toLowerCase()}`);
      setSubdomainStatus({
        checking: false,
        available: response.data.available,
        suggestion: response.data.suggestion,
        reason: response.data.reason
      });
    } catch (err) {
      setSubdomainStatus({ checking: false, available: null, suggestion: null });
    }
  };

  // Auto-suggest subdomain when organization name changes
  const handleOrganizationNameChange = async (value) => {
    setFormData(prev => ({ ...prev, organization_name: value }));
    
    // Auto-suggest subdomain if user hasn't manually entered one
    if (!formData.subdomain && value.length >= 2) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/workspaces/suggest-subdomain?name=${encodeURIComponent(value)}`);
        if (response.data.suggestion) {
          setFormData(prev => ({ ...prev, subdomain: response.data.suggestion }));
          setSubdomainStatus({
            checking: false,
            available: response.data.available !== false,
            suggestion: null
          });
        }
      } catch (err) {
        // Ignore errors in auto-suggest
      }
    }
  };

  // Debounced subdomain check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.subdomain) {
        checkSubdomainAvailability(formData.subdomain);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.subdomain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    // Subdomain validation
    if (formData.subdomain && subdomainStatus.available === false) {
      setError('Please choose an available workspace URL');
      setLoading(false);
      return;
    }

    const result = await register(formData.name, formData.email, formData.password, formData.organization_name, formData.subdomain);

    if (result.success) {
      // Check if we should redirect to workspace
      // Skip redirect in Emergent preview environment or localhost
      const isPreviewEnv = window.location.hostname.includes('emergentagent.com') || 
                           window.location.hostname.includes('emergent.host') ||
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';
      
      if (!isPreviewEnv && result.workspace && result.workspace.url) {
        // Get the token to pass to subdomain (cross-domain auth)
        const token = localStorage.getItem('token');
        // Redirect to the new workspace URL with token
        const redirectUrl = `${result.workspace.url}?auth_token=${encodeURIComponent(token)}`;
        window.location.href = redirectUrl;
      } else {
        // Fallback to normal flow (preview env or no workspace)
        onBack();
      }
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  return (
    <AuthSurface>
      <SEO 
        title="Sign Up | Lexa.run"
        description="Create your free Lexa.run account and start converting plain English into automated tests. Get started with AI-powered test automation today."
      />
      <div className="w-full max-w-md">
        <AuthCard>
          <AuthBrandHeader onBack={onBack} />

          <AuthHeading title="Get Started" description="Create your account to start building automated tests" />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <Input
                id="signup-name"
                autoComplete="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter your full name"
                className="h-12"
              />
            </div>

            <div>
              <label htmlFor="signup-organization" className="block text-sm font-medium text-gray-700 mb-2">
                Organization Name
              </label>
              <Input
                id="signup-organization"
                autoComplete="organization"
                type="text"
                required
                value={formData.organization_name}
                onChange={(e) => handleOrganizationNameChange(e.target.value)}
                placeholder="Enter your company or organization name"
                className="h-12"
                data-testid="register-organization-input"
              />
            </div>

            <div>
              <label htmlFor="signup-subdomain" className="block text-sm font-medium text-gray-700 mb-2">
                Workspace URL
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <Input
                    id="signup-subdomain"
                    autoComplete="off"
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) => setFormData({...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    placeholder="your-workspace"
                    className={`h-12 pr-10 ${
                      subdomainStatus.available === true ? 'border-green-500 focus:border-green-500' :
                      subdomainStatus.available === false ? 'border-red-500 focus:border-red-500' : ''
                    }`}
                    data-testid="register-subdomain-input"
                  />
                  {subdomainStatus.checking && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Clock className="h-4 w-4 animate-spin text-gray-400" />
                    </div>
                  )}
                  {!subdomainStatus.checking && subdomainStatus.available === true && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Check className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                  {!subdomainStatus.checking && subdomainStatus.available === false && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <X className="h-4 w-4 text-red-500" />
                    </div>
                  )}
                </div>
                <span className="text-gray-500 text-sm whitespace-nowrap">.lexa.run</span>
              </div>
              {subdomainStatus.available === false && subdomainStatus.reason && (
                <p className="text-xs text-red-600 mt-1">{subdomainStatus.reason}</p>
              )}
              {subdomainStatus.available === false && subdomainStatus.suggestion && (
                <p className="text-xs text-slate-500 mt-1">
                  Try: <button 
                    type="button"
                    onClick={() => setFormData({...formData, subdomain: subdomainStatus.suggestion})}
                    className="text-purple-600 hover:underline font-medium"
                  >
                    {subdomainStatus.suggestion}
                  </button>
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                This will be your team's unique workspace URL
              </p>
            </div>

            <div>
              <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <Input
                id="signup-email"
                autoComplete="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="Enter your email"
                className="h-12"
              />
            </div>

            <div>
              <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  id="signup-password"
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Create a password (min. 6 characters)"
                  className="h-12 pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#6d28d9] hover:bg-[#5b21b6] text-white font-medium"
            >
              {loading ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500">
              Already have an account?{' '}
              <button
                onClick={onSwitchToLogin}
                className="text-violet-700 hover:text-violet-800 font-medium"
              >
                Sign In
              </button>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Start with our <span className="font-medium text-purple-600">Free Plan</span> and explore Lexa's testing tools.
            </p>
          </div>
        </AuthCard>
      </div>
    </AuthSurface>
  );
};

// Team Management Component (Business/Enterprise only)
const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [loading, setLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, type: '', data: null });
  
  // Organization settings
  const [organization, setOrganization] = useState(null);
  const [showOrgSettings, setShowOrgSettings] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '', logo_url: '' });
  
  const { user } = useAuth();

  useEffect(() => {
    loadTeamData();
    loadOrganization();
  }, []);
  
  const loadOrganization = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/team/organization`);
      if (response.data) {
        setOrganization(response.data);
        setOrgForm({ 
          name: response.data.name || '', 
          logo_url: response.data.logo_url || '' 
        });
      }
    } catch (error) {
      console.error('Failed to load organization:', error);
    }
  };
  
  const updateOrganization = async () => {
    try {
      setLoading(true);
      await axios.put(`${BACKEND_URL}/api/team/organization`, orgForm);
      showSuccess('Organization settings updated successfully!');
      setShowOrgSettings(false);
      await loadOrganization();
    } catch (error) {
      console.error('Failed to update organization:', error);
      showError('Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamData = async () => {
    try {
      // Get team members and pending invites
      const membersResponse = await axios.get(`${BACKEND_URL}/api/team/members`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Extract members and pending_invites from response
      const data = membersResponse.data || {};
      setTeamMembers(data.members || []);
      setPendingInvites(data.pending_invites || []);
      
    } catch (error) {
      console.error('Failed to load team data:', error);
      setTeamMembers([]);
      setPendingInvites([]);
    }
  };

  const inviteTeamMember = async () => {
    if (!inviteForm.email.trim()) {
      showWarning('Please enter an email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/team/invite`, inviteForm);
      
      // Show success message with invitation details
      if (response.data.invitation_link) {
        showSuccess(`Team invitation sent to ${inviteForm.email}! Invitation link: ${response.data.invitation_link}`);
      } else {
        showSuccess(`Team invitation sent to ${inviteForm.email}!`);
      }
      
      setInviteForm({ email: '', role: 'member' });
      setShowInviteDialog(false);
      await loadTeamData();
    } catch (error) {
      console.error('Failed to invite team member:', error);
      let errorMessage = 'Failed to send invite';
      
      if (error.response?.data?.detail) {
        // Handle both string and object error details
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to invite team members. Upgrade to Business or Enterprise plan.';
      } else if (error.response?.status === 409) {
        errorMessage = 'This user is already part of your team or has a pending invitation.';
      } else if (error.response?.status >= 400) {
        errorMessage = `Error ${error.response.status}: ${error.response.statusText || 'Request failed'}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resendInvite = async (inviteId, email) => {
    setConfirmDialog({
      show: true,
      type: 'resend',
      data: { inviteId, email },
      title: 'Resend Invitation',
      message: `Resend invitation to ${email}?`
    });
  };

  const confirmResendInvite = async () => {
    const { inviteId, email } = confirmDialog.data;
    setConfirmDialog({ show: false, type: '', data: null });
    
    try {
      await axios.post(`${BACKEND_URL}/api/team/invite/${inviteId}/resend`);
      showSuccess(`Invitation resent to ${email}`);
      await loadTeamData();
    } catch (error) {
      console.error('Failed to resend invite:', error);
      showError('Failed to resend invitation');
    }
  };

  const removeTeamMember = async (memberId, memberName) => {
    setConfirmDialog({
      show: true,
      type: 'remove',
      data: { memberId, memberName },
      title: 'Remove Team Member',
      message: `Are you sure you want to remove ${memberName || 'this team member'}?`
    });
  };

  const confirmRemoveTeamMember = async () => {
    const { memberId } = confirmDialog.data;
    setConfirmDialog({ show: false, type: '', data: null });

    try {
      await axios.delete(`${BACKEND_URL}/api/team/members/${memberId}`);
      showSuccess('Team member removed successfully');
      await loadTeamData();
    } catch (error) {
      console.error('Failed to remove team member:', error);
      showError('Failed to remove team member');
    }
  };

  const handleConfirmAction = () => {
    if (confirmDialog.type === 'resend') {
      confirmResendInvite();
    } else if (confirmDialog.type === 'remove') {
      confirmRemoveTeamMember();
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/team/invite/${inviteId}`);
      await loadTeamData();
    } catch (error) {
      console.error('Failed to cancel invite:', error);
      showError('Failed to cancel invite');
    }
  };

  const updateMemberRole = async (memberId, newRole) => {
    try {
      await axios.put(`${BACKEND_URL}/api/team/members/role`, {
        user_id: memberId,
        new_role: newRole
      });
      await loadTeamData();
    } catch (error) {
      console.error('Failed to update member role:', error);
      showError('Failed to update member role');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'member': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-600" />
            Team Management
          </h3>
          <p className="text-sm text-gray-600">
            Manage your team members and their roles
          </p>
        </div>

        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation to add a new member to your team.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Role
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <Button 
                onClick={inviteTeamMember}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
              >
                {loading ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Sending Invite...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invite
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Organization Settings */}
      {organization && (
        <Card className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-semibold text-lg flex items-center">
                <Building className="h-5 w-5 mr-2 text-purple-600" />
                Organization Settings
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Company name and branding for your team
              </p>
              <div className="mt-3 space-y-2">
                <p className="text-sm"><span className="font-medium">Organization:</span> {organization.name}</p>
                {organization.logo_url && (
                  <img src={organization.logo_url} alt="Logo" className="h-12 w-auto" />
                )}
              </div>
            </div>
            <Button
              onClick={() => setShowOrgSettings(true)}
              variant="outline"
              size="sm"
            >
              <Settings className="h-4 w-4 mr-2" />
              Edit Settings
            </Button>
          </div>
        </Card>
      )}
      
      {/* Organization Settings Dialog */}
      <Dialog open={showOrgSettings} onOpenChange={setShowOrgSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Organization Settings</DialogTitle>
            <DialogDescription>
              Configure your organization details and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Organization / Company Name
              </label>
              <Input
                type="text"
                placeholder="Acme Inc."
                value={orgForm.name}
                onChange={(e) => setOrgForm({...orgForm, name: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Logo URL (optional)
              </label>
              <Input
                type="url"
                placeholder="https://example.com/logo.png"
                value={orgForm.logo_url}
                onChange={(e) => setOrgForm({...orgForm, logo_url: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter a URL to your company logo image
              </p>
            </div>

            <Button 
              onClick={updateOrganization}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Members & Pending Invites */}
      <Card className="p-6">
        <h4 className="font-semibold mb-4">Team Members ({teamMembers.length + pendingInvites.length})</h4>
        <div className="space-y-3">
          {/* Active Team Members */}
          {teamMembers.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{member.name}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                  <p className="text-xs text-green-600 font-medium">Active</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Badge className={getRoleBadgeColor(member.role)}>
                  {member.role}
                </Badge>

                {!member.is_owner && (
                  <div className="flex space-x-1">
                    <select
                      value={member.role}
                      onChange={(e) => updateMemberRole(member.id, e.target.value)}
                      className="text-xs p-1 border border-gray-300 rounded"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    
                    <Button
                      onClick={() => removeTeamMember(member.id, member.name || member.email)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Pending Invites */}
          {pendingInvites.map((invite) => (
            <div key={invite.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{invite.email}</p>
                  <p className="text-xs text-yellow-600 font-medium">Invitation Sent</p>
                  <p className="text-xs text-gray-500">
                    Invited {new Date(invite.invited_at || invite.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Badge className={getRoleBadgeColor(invite.role)}>
                  {invite.role}
                </Badge>
                <Button
                  onClick={() => resendInvite(invite.id, invite.email)}
                  variant="outline"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  title="Resend Invite"
                >
                  <Mail className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => cancelInvite(invite.id)}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="Cancel Invite"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {teamMembers.length === 0 && pendingInvites.length === 0 && (
            <p className="text-gray-500 text-center py-4">
              No team members yet. Invite your first team member!
            </p>
          )}
        </div>
      </Card>

      {/* Team Limits */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-center space-x-2">
          <Shield className="h-5 w-5 text-blue-600" />
          <p className="text-sm text-blue-800">
            <strong>{user?.plan === 'business' ? 'Business Plan' : 'Enterprise Plan'}:</strong> 
            {user?.plan === 'business' ? ' Up to 3 team members' : ' Unlimited team members'}
          </p>
        </div>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.show} onOpenChange={(open) => !open && setConfirmDialog({ show: false, type: '', data: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogDescription>
              {confirmDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ show: false, type: '', data: null })}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              className="flex-1"
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// User Profile Modal Component
const UserProfileModal = ({ user, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/auth/profile`,
        {
          name: profileForm.name.trim(),
          email: profileForm.email.trim()
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        onSuccess(response.data);
      }, 1500);
      
    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      setError('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/auth/change-password`,
        passwordForm,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setSuccess('Password changed successfully! A confirmation email has been sent.');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
    } catch (error) {
      console.error('Password change error:', error);
      setError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">User Profile</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b mb-4">
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'security'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('security')}
            >
              Security
            </button>
          </div>
          
          {/* Messages */}
          {error && (
            <Alert className="mb-4 border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="mb-4 border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <Input
                  id="name"
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  placeholder="Your name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  placeholder="your.email@company.com"
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
          
          {/* Security Tab */}
          {activeTab === 'security' && (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Enter current password"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                />
              </div>
              
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
// Subscription Plans Component
const SubscriptionPlans = ({ globalUsage, globalDetailedUsage, fetchCurrentUsageData, handleUpgradePlan }) => {
  const [plans, setPlans] = useState({});
  const [usage, setUsage] = useState(globalUsage);
  const [detailedUsage, setDetailedUsage] = useState(globalDetailedUsage);
  const [loading, setLoading] = useState(false);
  const [overageAmount, setOverageAmount] = useState(100); // Default overage purchase amount
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const { user } = useAuth();
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [, setRefreshTick] = useState(0); // For triggering re-renders of time display

  // Update local state when global state changes
  useEffect(() => {
    setUsage(globalUsage);
    setDetailedUsage(globalDetailedUsage);
  }, [globalUsage, globalDetailedUsage]);

  useEffect(() => {
    loadPlans();
    // Don't load usage here since we're using global state
  }, [user]);

  // Auto-refresh usage data every 30 seconds
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    const refreshInterval = setInterval(async () => {
      if (fetchCurrentUsageData) {
        await fetchCurrentUsageData();
        setLastRefresh(new Date());
      }
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [autoRefreshEnabled, fetchCurrentUsageData]);

  // Update the "time since refresh" display every 5 seconds
  useEffect(() => {
    const displayInterval = setInterval(() => {
      setRefreshTick(tick => tick + 1); // Trigger re-render
    }, 5000);
    
    return () => clearInterval(displayInterval);
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };

  const loadUsage = async () => {
    // Fetch current usage data (read-only)
    if (fetchCurrentUsageData) {
      await fetchCurrentUsageData();
    } else {
      // Fallback to local loading if global function not available
      try {
        const response = await axios.get(`${BACKEND_URL}/api/subscription/current`);
        setUsage(response.data);
      } catch (error) {
        console.error('Failed to load usage:', error);
      }
    }
  };

  const loadDetailedUsage = async () => {
    // Fetch current usage data (read-only)
    if (fetchCurrentUsageData) {
      await fetchCurrentUsageData();
    } else {
      // Fallback to local loading if global function not available
      try {
        const response = await axios.get(`${BACKEND_URL}/api/subscription/usage`);
        setDetailedUsage(response.data);
      } catch (error) {
        console.error('Failed to load detailed usage:', error);
        setDetailedUsage(null);
      }
    }
  };

  const upgradePlan = async (planId) => {
    setLoading(true);
    try {
      // Use new plan change endpoint
      const response = await axios.post(`${BACKEND_URL}/api/subscription/change-plan`, {
        new_plan_code: planId.toUpperCase(),
        effective_immediately: true
      });
      showSuccess(`Successfully upgraded to ${planId} plan!`);
      
      // Reload usage data
      await loadUsage();
      await loadDetailedUsage();
    } catch (error) {
      console.error('Upgrade failed:', error);
      let errorMessage = 'Upgrade failed. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Upgrade failed. Please try again.';
      }
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const purchaseOverage = async () => {
    if (!detailedUsage?.overage_enabled) {
      showError('Overage is not available for your current plan');
      return;
    }

    if (overageAmount < 1) {
      showWarning('Please enter a valid number of additional executions');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscription/purchase-overage`, {
        additional_executions: parseInt(overageAmount)
      });
      
      showSuccess(`Successfully purchased ${overageAmount} additional executions for $${response.data.cost_usd}!`);
      
      // Reload usage data
      await loadUsage();
      await loadDetailedUsage();
    } catch (error) {
      console.error('Overage purchase failed:', error);
      let errorMessage = 'Purchase failed. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Purchase failed. Please try again.';
      }
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async () => {
    setShowCancelDialog(true);
  };

  const confirmCancelSubscription = async () => {
    setShowCancelDialog(false);
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscription/cancel`);
      showSuccess(response.data.message || 'Subscription cancelled successfully');
      
      // Reload usage data instead of full page reload
      await loadUsage();
      await loadDetailedUsage();
    } catch (error) {
      console.error('Cancellation failed:', error);
      let errorMessage = 'Cancellation failed. Please try again or contact support.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Cancellation failed. Please try again or contact support.';
      }
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getUserPlanId = () => {
    return usage?.current_plan || user?.plan || 'TRIAL';
  };

  const formatPlanName = (planId) => {
    if (!planId) return 'Trial';
    return planId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Use detailed usage if available, fallback to basic usage
  const currentUsage = detailedUsage || usage;

  // Manual refresh handler
  const handleManualRefresh = async () => {
    if (fetchCurrentUsageData) {
      await fetchCurrentUsageData();
      setLastRefresh(new Date());
    }
  };

  // Format time since last refresh
  const getTimeSinceRefresh = () => {
    const seconds = Math.floor((new Date() - lastRefresh) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  return (
    <div className="lexa-app-plans mx-auto max-w-7xl space-y-8">
      {/* Enhanced Current Usage Card */}
      {currentUsage && (
        <Card className="p-6 rounded-2xl border-slate-200 bg-white shadow-none">
          <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
            <h3 className="text-lg font-semibold flex flex-wrap gap-y-2 items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
              Current Usage
              {/* Auto-refresh indicator */}
              <span className="sm:ml-3 flex flex-wrap items-center text-xs font-normal text-gray-500">
                <span className={`w-2 h-2 rounded-full mr-1.5 ${autoRefreshEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                {autoRefreshEnabled ? 'Live' : 'Paused'}
                <span className="mx-1.5">•</span>
                Updated {getTimeSinceRefresh()}
                <button 
                  onClick={handleManualRefresh}
                  className="ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  title="Refresh now"
                >
                  <RefreshCw className="h-3.5 w-3.5 text-gray-500 hover:text-blue-600" />
                </button>
                <button 
                  onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                  className="ml-1 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  title={autoRefreshEnabled ? 'Pause auto-refresh' : 'Resume auto-refresh'}
                >
                  {autoRefreshEnabled ? (
                    <Pause className="h-3.5 w-3.5 text-gray-500 hover:text-orange-600" />
                  ) : (
                    <Play className="h-3.5 w-3.5 text-gray-500 hover:text-green-600" />
                  )}
                </button>
              </span>
            </h3>
            {/* Cancel Subscription Button - only show for admins with paid plans */}
            {user?.role !== 'member' && getUserPlanId() !== 'TRIAL' && getUserPlanId() !== 'free' && (
              <Button
                variant="outline"
                size="sm"
                onClick={cancelSubscription}
                disabled={loading}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Subscription'
                )}
              </Button>
            )}
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Plan Information */}
              <div className="text-center p-2 sm:p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-950">
                  {formatPlanName(getUserPlanId())}
                </div>
                <div className="text-xs text-slate-500">Current Plan</div>
                {currentUsage.is_trial && (
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs mt-1">
                    Trial
                  </Badge>
                )}
              </div>

              {/* Executions Used */}
              <div className="text-center p-2 sm:p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-950">
                  {currentUsage.executions_used !== undefined ? currentUsage.executions_used : (currentUsage.monthly_tests_used || 0)}
                </div>
                <div className="text-xs text-slate-500">Used This Month</div>
              </div>

              {/* Executions Remaining */}
              <div className="text-center p-2 sm:p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-950">
                  {currentUsage.executions_remaining !== undefined 
                    ? currentUsage.executions_remaining 
                    : (typeof currentUsage.tests_remaining === 'string' ? currentUsage.tests_remaining : (currentUsage.tests_remaining || 0))}
                </div>
                <div className="text-xs text-slate-500">Remaining</div>
              </div>

              {/* Usage Percentage */}
              <div className="text-center p-2 sm:p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-950">
                  {currentUsage.usage_percentage !== undefined 
                    ? `${Math.round(currentUsage.usage_percentage)}%`
                    : `${Math.round(((currentUsage.monthly_tests_used || 0) / (currentUsage.monthly_test_limit || 1)) * 100)}%`}
                </div>
                <div className="text-xs text-slate-500">Usage Rate</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex flex-wrap justify-between gap-2 text-sm">
                <span>Monthly Executions</span>
                <span>
                  {currentUsage.executions_used !== undefined ? currentUsage.executions_used : (currentUsage.monthly_tests_used || 0)} / {' '}
                  {(currentUsage.executions_limit || currentUsage.monthly_test_limit) === -1 
                    ? 'Unlimited' 
                    : (currentUsage.executions_limit || currentUsage.monthly_test_limit || 0)}
                </span>
              </div>
              <Progress 
                value={
                  (currentUsage.executions_limit || currentUsage.monthly_test_limit) === -1 
                    ? 0 
                    : currentUsage.usage_percentage !== undefined
                    ? currentUsage.usage_percentage
                    : (((currentUsage.monthly_tests_used || 0) / (currentUsage.monthly_test_limit || 1)) * 100)
                }
                className="h-3"
              />
            </div>

            {/* Enhanced Usage Information */}
            {detailedUsage && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                {/* Billing Period */}
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">Billing Period</div>
                  <div className="text-xs text-gray-500">
                    {new Date(detailedUsage.period_start).toLocaleDateString()} - {' '}
                    {new Date(detailedUsage.period_end).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    {detailedUsage.days_until_reset} days until reset
                  </div>
                </div>

                {/* Overage Information */}
                {detailedUsage.overage_enabled && (
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-700">Overage</div>
                    <div className="text-xs text-gray-500">
                      {detailedUsage.overage_units} units used
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      ${detailedUsage.overage_price_per_unit}/execution
                    </div>
                  </div>
                )}

                {/* Trial Information */}
                {detailedUsage.is_trial && detailedUsage.trial_ends_at && (
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-700">Trial Expires</div>
                    <div className="text-xs text-slate-500">
                      {new Date(detailedUsage.trial_ends_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Overage Purchase Section */}
            {detailedUsage?.overage_enabled && detailedUsage?.limit_reached && (
              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <div className="flex items-center space-x-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">Usage Limit Reached</span>
                </div>
                <p className="text-sm text-yellow-700 mb-3">
                  Purchase additional executions to continue testing
                </p>
                <div className="flex items-center space-x-2">
                  <Input
                    type="number"
                    value={overageAmount}
                    onChange={(e) => setOverageAmount(e.target.value)}
                    placeholder="100"
                    className="w-24"
                    min="1"
                  />
                  <span className="text-sm text-gray-600">executions</span>
                  <Button
                    onClick={purchaseOverage}
                    disabled={loading}
                    size="sm"
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    Purchase for ${(overageAmount * (detailedUsage?.overage_price_per_unit || 0)).toFixed(2)}
                  </Button>
                </div>
              </Card>
            )}

            {/* Usage Warnings */}
            {currentUsage.limit_warning && (
              <Alert className="border-orange-200 bg-orange-50">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  You've used {Math.round(currentUsage.usage_percentage || 0)}% of your monthly quota.
                  Consider upgrading your plan.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </Card>
      )}

      {/* Plan choices keep the existing authenticated checkout/change callbacks. */}
      {user?.role !== 'member' && (
        <PlanCards
          plans={plans}
          currentPlan={getUserPlanId()}
          loading={loading}
          onDowngrade={upgradePlan}
          onUpgrade={handleUpgradePlan}
        />
      )}

      {/* Overage Purchase Section - Only visible to admins */}
      {user?.role !== 'member' && (
        <>
          {currentUsage && currentUsage.current_plan !== 'TRIAL' && (
            <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6">
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Need More Test Executions?
                </h3>
                <p className="text-gray-600 mb-4">
                  Purchase additional test executions at competitive rates
                </p>
                <Button
                  onClick={() => handleUpgradePlan && handleUpgradePlan('OVERAGE')}
                  className="bg-[#6d28d9] hover:bg-[#5b21b6] rounded-full text-white whitespace-normal h-auto min-h-10"
                >
                  Purchase Additional Executions
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You will immediately be downgraded to the Trial plan.
            </DialogDescription>
          </DialogHeader>
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              className="flex-1"
            >
              Keep Subscription
            </Button>
            <Button
              onClick={confirmCancelSubscription}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Cancel Subscription
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = ({ initialView = 'dashboard', renderLayout, onTabChange, onOpenProfile }) => {
  const projectFeedback = useSuitesProjectsLanguage();
  const { user, logout, updateUser } = useAuth();
  const [testCases, setTestCases] = useState([]);
  const [flowTests, setFlowTests] = useState([]);
  const [allTests, setAllTests] = useState([]); // Combined API tests and Flow tests
  const [loading, setLoading] = useState(false);
  const [newTest, setNewTest] = useState({
    scenarioName: '',
    description: '',
    website_url: ''
  });
  const [formErrors, setFormErrors] = useState({
    scenarioName: '',
    description: '',
    website_url: ''
  });
  const [selectedTest, setSelectedTest] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [showOverageModal, setShowOverageModal] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationRef = useRef(null);
  const [executions, setExecutions] = useState([]);
  const [activeTab, setActiveTab] = useState(initialView);
  const [currentPath, setCurrentPath] = useState(window.location.pathname); // Track path for routing
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // Sync activeTab with initialView when it changes from sidebar
  useEffect(() => {
    if (initialView !== activeTab) {
      setActiveTab(initialView);
    }
  }, [initialView]);

  // Handle URL-based navigation for Sprint QA
  useEffect(() => {
    const handleSprintQARouting = () => {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      const viewParam = params.get('view');
      
      setCurrentPath(path); // Update path state to trigger re-render
      
      // Only set activeTab to sprint-qa if:
      // 1. Path starts with /sprint-qa AND
      // 2. There's no view parameter (or view param is sprint-qa)
      if (path.startsWith('/sprint-qa') && (!viewParam || viewParam === 'sprint-qa')) {
        setActiveTab('sprint-qa');
      }
    };
    
    // Handle custom navigation event from SprintQAProjects
    const handleSprintQANavigate = (e) => {
      if (e.detail) {
        setCurrentPath(e.detail);
        setActiveTab('sprint-qa');
      }
    };
    
    // Check on mount
    handleSprintQARouting();
    
    // Listen for URL changes
    window.addEventListener('popstate', handleSprintQARouting);
    
    // Listen for Sprint QA navigation events
    window.addEventListener('sprintQANavigate', handleSprintQANavigate);
    
    // Listen for custom tab change events
    const handleTabChange = (e) => {
      if (e.detail) {
        setActiveTab(e.detail);
      }
    };
    window.addEventListener('changeTab', handleTabChange);
    
    return () => {
      window.removeEventListener('popstate', handleSprintQARouting);
      window.removeEventListener('sprintQANavigate', handleSprintQANavigate);
      window.removeEventListener('changeTab', handleTabChange);
    };
  }, []);

  // Global usage state for the entire dashboard
  const [globalUsage, setGlobalUsage] = useState(null);
  const [globalDetailedUsage, setGlobalDetailedUsage] = useState(null);

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamInvites, setTeamInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamConfirmDialog, setTeamConfirmDialog] = useState({ show: false, type: '', data: null });
  
  // Organization settings state
  const [organization, setOrganization] = useState(null);
  const [showOrgSettings, setShowOrgSettings] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '', logo_url: '' });
  const [orgLoading, setOrgLoading] = useState(false);
  
  // Project management states
  const [projects, setProjects] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    icon: '📁',
    color: '#6366F1'
  });
  const [projectLoading, setProjectLoading] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  
  // Support ticket state
  const [showSupportTicket, setShowSupportTicket] = useState(false);

  // Fetch latest usage data (READ-ONLY - does not modify billing data)
  const fetchCurrentUsageData = async () => {
    try {
      debugLog('📊 Fetching current usage data (read-only)...');
      const [usageResponse, detailedResponse] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/subscription/current`),
        axios.get(`${BACKEND_URL}/api/subscription/usage`)
      ]);

      if (usageResponse.status === 'fulfilled') {
        setGlobalUsage(usageResponse.value.data);
        debugLog('✅ Current subscription data fetched');
      }
      if (detailedResponse.status === 'fulfilled') {
        setGlobalDetailedUsage(detailedResponse.value.data);
        debugLog('✅ Current usage statistics fetched');
      }
    } catch (error) {
      console.error('Failed to fetch current usage data:', error);
    }
  };

  // Fetch all current data without modifying any billing information
  const fetchLatestDashboardData = async () => {
    try {
      debugLog('📋 Fetching latest dashboard data (read-only)...');
      
      // Fetch current usage data (does not modify billing)
      await fetchCurrentUsageData();
      
      // Fetch team data if user has team access
      if (hasTeamAccess()) {
        await loadTeamData();
        await loadOrganization();
      }
      
      // Overview loads scoped metrics independently of legacy test handlers.
      
      debugLog('✅ Latest dashboard data fetched successfully');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Stripe Payment Handlers
  const handleUpgradePlan = (planCode) => {
    if (planCode === 'OVERAGE') {
      setShowOverageModal(true);
    } else {
      setSelectedPlanForPayment(planCode);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setSelectedPlanForPayment(null);
    setShowOverageModal(false);
    
    // Refresh subscription and usage data
    await fetchLatestDashboardData();
    
    // Show success message
    showSuccess('Payment successful! Your subscription has been updated.');
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setSelectedPlanForPayment(null);
    setShowOverageModal(false);
  };

  const handleOveragePurchase = () => {
    setShowOverageModal(true);
  };

  // Load current data on component mount (read-only operations)
  useEffect(() => {
    if (user) {
      // Fetch latest data to ensure UI shows current state
      fetchLatestDashboardData();
    }
  }, [user]);

  // Notification functions - Connected to Backend API
  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications`, {
        params: { limit: 50 },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setNotifications(response.data);
        setUnreadCount(response.data.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Silently fail - don't disrupt user experience
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      // Optimistically update UI
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Call backend API
      await axios.put(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert on error by refetching
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    try {
      // Optimistically update UI
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      setUnreadCount(0);
      
      // Call backend API
      await axios.put(`${BACKEND_URL}/api/notifications/mark-all-read`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Revert on error by refetching
      fetchNotifications();
    }
  };

  const clearAllNotifications = async () => {
    try {
      // Optimistically update UI
      setNotifications([]);
      setUnreadCount(0);
      
      // Call backend API to delete all notifications
      await axios.delete(`${BACKEND_URL}/api/notifications`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      // Revert on error by refetching
      fetchNotifications();
    }
  };

  // Handle notification click to redirect to appropriate test history
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate based on notification type
    if (notification.type === 'support_reply') {
      // Store the ticket ID to open it
      if (notification.metadata && notification.metadata.ticket_id) {
        sessionStorage.setItem('openTicketId', notification.metadata.ticket_id);
      }
      
      // Navigate to support page
      setActiveTab('support');
      
      // Also notify parent if callback exists
      if (onTabChange) {
        onTabChange('support');
      }
    } else if (notification.test_type === 'api') {
      setActiveTab('api-testing');
      if (onTabChange) onTabChange('api-testing');
      // The APITesting component will need to handle showing history for specific test
    } else if (notification.test_type === 'flow') {
      setActiveTab('flow-testing');
      if (onTabChange) onTabChange('flow-testing');
      // The FlowTesting component will need to handle showing history for specific test
    }
    
    setShowNotifications(false);
  };

  // Load notifications from backend on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000); // Poll every 30 seconds
    
    return () => clearInterval(interval);
  }, [user]);

  // Handle click outside to close notification dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    // Only add event listener if notifications are shown
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Team management functions
  const loadTeamData = async () => {
    // Skip loading team data for members (they don't have access to team management)
    if (user?.role === 'member') {
      return;
    }
    
    try {
      const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }};
      
      // Get team members and pending invites from single endpoint
      const membersResponse = await axios.get(`${BACKEND_URL}/api/team/members`, authHeaders);
      
      if (membersResponse.data) {
        const data = membersResponse.data;
        setTeamMembers(data.members || []);
        setTeamInvites(data.pending_invites || []); // Backend returns 'pending_invites'
      }
    } catch (error) {
      console.error('Failed to load team data:', error);
      setTeamMembers([]);
      setTeamInvites([]);
    }
  };

  const inviteTeamMember = async () => {
    if (!inviteEmail.trim()) {
      showWarning('Please enter a valid email address');
      return;
    }

    setTeamLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/team/invite`, {
        email: inviteEmail.trim()
      });
      
      setInviteEmail('');
      await loadTeamData();
      showSuccess('Invitation sent successfully!');
    } catch (error) {
      console.error('Failed to invite team member:', error);
      let errorMessage = 'Failed to send invitation. Please try again.';
      
      if (error.response?.data?.detail) {
        // Handle both string and object error details
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to invite team members. Please upgrade your plan.';
      } else if (error.response?.status === 409) {
        errorMessage = 'This user is already part of your team or has a pending invitation.';
      } else if (error.response?.status >= 400) {
        errorMessage = `Error ${error.response.status}: ${error.response.statusText || 'Failed to send invitation'}`;
      }
      
      showError(errorMessage);
    } finally {
      setTeamLoading(false);
    }
  };

  const removeTeamMember = async (memberId, memberName) => {
    setTeamConfirmDialog({
      show: true,
      type: 'remove',
      data: { memberId, memberName },
      title: 'Remove Team Member',
      message: `Are you sure you want to remove ${memberName || 'this team member'}?`
    });
  };

  const confirmRemoveTeamMember = async () => {
    const { memberId } = teamConfirmDialog.data;
    setTeamConfirmDialog({ show: false, type: '', data: null });

    setTeamLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/team/members/${memberId}`);
      await loadTeamData();
      showSuccess('Team member removed successfully');
    } catch (error) {
      console.error('Failed to remove team member:', error);
      let errorMessage = 'Failed to remove team member. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Failed to remove team member. Please try again.';
      }
      showError(errorMessage);
    } finally {
      setTeamLoading(false);
    }
  };

  const cancelInvite = async (inviteId, email) => {
    setTeamConfirmDialog({
      show: true,
      type: 'cancel',
      data: { inviteId, email },
      title: 'Cancel Invitation',
      message: `Are you sure you want to cancel the invitation to ${email}?`
    });
  };

  const confirmCancelInvite = async () => {
    const { inviteId } = teamConfirmDialog.data;
    setTeamConfirmDialog({ show: false, type: '', data: null });

    setTeamLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/team/invite/${inviteId}`);
      await loadTeamData();
      showSuccess('Invitation cancelled successfully');
    } catch (error) {
      console.error('Failed to cancel invite:', error);
      let errorMessage = 'Failed to cancel invitation. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Failed to cancel invitation. Please try again.';
      }
      showError(errorMessage);
    } finally {
      setTeamLoading(false);
    }
  };

  const resendInvite = async (inviteId, email) => {
    setTeamConfirmDialog({
      show: true,
      type: 'resend',
      data: { inviteId, email },
      title: 'Resend Invitation',
      message: `Resend invitation to ${email}?`
    });
  };

  const confirmResendInvite = async () => {
    const { inviteId, email } = teamConfirmDialog.data;
    setTeamConfirmDialog({ show: false, type: '', data: null });

    setTeamLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/team/invite/${inviteId}/resend`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showSuccess(`Invitation resent to ${email}`);
      await loadTeamData();
    } catch (error) {
      console.error('Failed to resend invite:', error);
      let errorMessage = 'Failed to resend invitation. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Failed to resend invitation. Please try again.';
      }
      showError(`Failed to resend: ${errorMessage}`);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleTeamConfirmAction = () => {
    if (teamConfirmDialog.type === 'remove') {
      confirmRemoveTeamMember();
    } else if (teamConfirmDialog.type === 'cancel') {
      confirmCancelInvite();
    } else if (teamConfirmDialog.type === 'resend') {
      confirmResendInvite();
    } else if (teamConfirmDialog.type === 'deleteProject') {
      confirmDeleteProject();
    } else if (teamConfirmDialog.type === 'deleteTest') {
      confirmDeleteTest();
    }
  };


  // Organization settings functions
  const loadOrganization = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/team/organization`);
      if (response.data) {
        setOrganization(response.data);
        setOrgForm({ 
          name: response.data.name || '', 
          logo_url: response.data.logo_url || '' 
        });
      }
    } catch (error) {
      console.error('Failed to load organization:', error);
    }
  };

  const updateOrganization = async () => {
    if (!orgForm.name.trim()) {
      showWarning('Organization name is required');
      return;
    }

    setOrgLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/team/organization`, orgForm);
      showSuccess('Organization settings updated successfully!');
      setShowOrgSettings(false);
      await loadOrganization();
    } catch (error) {
      console.error('Failed to update organization:', error);
      let errorMessage = 'Failed to update organization settings. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : errorMessage;
      }
      showError(`Failed to update: ${errorMessage}`);
    } finally {
      setOrgLoading(false);
    }
  };

  // Project management functions
  const loadProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    }
  };

  const createProject = async () => {
    if (!projectForm.name.trim()) {
      projectFeedback.showWarning('Project name is required');
      return;
    }

    setProjectLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/projects`, projectForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      projectFeedback.showSuccess('Project created successfully!');
      setShowProjectModal(false);
      setProjectForm({ name: '', description: '', icon: '📁', color: '#6366F1' });
      await loadProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      projectFeedback.showError('Failed to create project. Please try again.');
    } finally {
      setProjectLoading(false);
    }
  };

  const updateProject = async () => {
    if (!projectForm.name.trim()) {
      projectFeedback.showWarning('Project name is required');
      return;
    }

    setProjectLoading(true);
    try {
      await axios.put(`/api/projects/${editingProject.id}`, projectForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      projectFeedback.showSuccess('Project updated successfully!');
      setShowProjectModal(false);
      setEditingProject(null);
      setProjectForm({ name: '', description: '', icon: '📁', color: '#6366F1' });
      await loadProjects();
    } catch (error) {
      console.error('Failed to update project:', error);
      projectFeedback.showError('Failed to update project. Please try again.');
    } finally {
      setProjectLoading(false);
    }
  };

  const deleteProject = async (projectId, projectName) => {
    setTeamConfirmDialog({
      show: true,
      type: 'deleteProject',
      data: { projectId, projectName },
      title: projectFeedback.t('Delete Project'),
      message: projectFeedback.t('Are you sure you want to delete "{name}"? Tests will remain but won\'t be organized.', { name: projectName })
    });
  };

  const confirmDeleteProject = async () => {
    const { projectId } = teamConfirmDialog.data;
    setTeamConfirmDialog({ show: false, type: '', data: null });

    try {
      await axios.delete(`/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      projectFeedback.showSuccess('Project deleted successfully!');
      await loadProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      projectFeedback.showError('Failed to delete project. Please try again.');
    }
  };

  const openEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      description: project.description || '',
      icon: project.icon || '📁',
      color: project.color || '#6366F1'
    });
    setShowProjectModal(true);
  };

  // Null check guard - if user is null, don't render Dashboard
  if (!user) {
    return null;
  }

  // Load projects on mount; Overview owns its quality metrics request.
  useEffect(() => {
    if (user) {
      loadProjects(); // Load projects for team tab
    }
  }, [user]);

  const loadTestCases = async () => {
    try {
      // Load ALL API test cases without pagination limits
      const response = await axios.get(`${BACKEND_URL}/api/test-cases?limit=10000`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const apiTestsData = response.data || [];
      
      debugLog(`📊 Loaded ${apiTestsData.length} API test cases for dashboard stats`);
      setTestCases(apiTestsData);
    } catch (error) {
      console.error('Failed to load test cases:', error);
      setTestCases([]);
    }
  };

  const loadFlowTests = async () => {
    try {
      // Load ALL Flow tests without pagination limits
      const response = await axios.get(`${BACKEND_URL}/api/flow-testing/tests?limit=10000`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      // API returns {tests: [], total: ...} so extract the tests array
      const flowTestsData = response.data.tests || response.data || [];
      
      debugLog(`📊 Loaded ${flowTestsData.length} Flow tests for dashboard stats`);
      
      // For each Flow Test, get comprehensive execution data to determine dashboard stats
      const transformedFlowTests = await Promise.all(
        flowTestsData.map(async (flowTest) => {
          let dashboardStatus = 'draft'; // Default status
          let executionSummary = null;
          
          // If the test has been executed, analyze ALL executions for better dashboard representation
          if (flowTest.execution_count > 0 && flowTest.last_executed_at) {
            try {
              // Get recent executions (last 10) to determine overall test status
              const executionsResponse = await axios.get(
                `${BACKEND_URL}/api/flow-testing/tests/${flowTest.id}/executions?limit=10`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
              );
              
              if (executionsResponse.data && executionsResponse.data.length > 0) {
                const executions = executionsResponse.data;
                const latestExecution = executions[0];
                
                // Calculate execution summary for better dashboard representation
                const summary = executions.reduce((acc, exec) => {
                  if (exec.status === 'passed') acc.passed++;
                  else if (exec.status === 'failed' || exec.status === 'error') acc.failed++;
                  acc.total++;
                  return acc;
                }, { passed: 0, failed: 0, total: 0 });
                
                executionSummary = summary;
                
                // Dashboard status logic:
                // 1. If latest execution is passed/failed, use that
                // 2. If latest is running/pending, use the last completed execution
                // 3. Consider overall success rate for better representation
                if (latestExecution.status === 'passed' || latestExecution.status === 'failed' || latestExecution.status === 'error') {
                  dashboardStatus = latestExecution.status;
                } else {
                  // Find the most recent completed execution
                  const completedExecution = executions.find(exec => 
                    exec.status === 'passed' || exec.status === 'failed' || exec.status === 'error'
                  );
                  if (completedExecution) {
                    dashboardStatus = completedExecution.status;
                  }
                }
                
                debugLog(`📊 Flow Test "${flowTest.name}": ${summary.passed}/${summary.total} passed, latest: ${latestExecution.status}`);
              }
            } catch (execError) {
              console.warn(`Failed to load execution data for Flow Test ${flowTest.id}:`, execError);
            }
          }
          
          return {
            ...flowTest,
            type: 'flow_test',
            name: flowTest.name,
            created_at: flowTest.created_at,
            dashboardStatus: dashboardStatus,
            flowTestStatus: flowTest.status,
            executionSummary: executionSummary,
            // Include more execution metadata for debugging
            hasExecutions: flowTest.execution_count > 0,
            executionCount: flowTest.execution_count || 0
          };
        })
      );
      
      setFlowTests(transformedFlowTests);
    } catch (error) {
      console.error('Failed to load flow tests:', error);
      setFlowTests([]);
    }
  };

  const loadAllTests = async () => {
    try {
      debugLog('📊 Starting comprehensive test data loading for dashboard...');
      await Promise.all([loadTestCases(), loadFlowTests()]);
      debugLog('📊 Completed loading all test data for dashboard stats');
    } catch (error) {
      console.error('Failed to load all tests:', error);
    }
  };

  // Force refresh all dashboard data
  const refreshDashboardData = async () => {
    debugLog('🔄 Force refreshing dashboard data...');
    setLoading(true);
    try {
      await loadAllTests();
    } catch (error) {
      console.error('Failed to refresh dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to test execution history
  const navigateToTestHistory = (test) => {
    debugLog(`🔗 Navigating to execution history for ${test.type} test: ${test.name}`);
    
    // Set the selected test for the appropriate component to pick up
    setSelectedTest(test);
    
    const target = test.type === 'flow_test' ? 'flow-testing' : 'api-testing';
    setActiveTab(target);
    if (onTabChange) onTabChange(target);
    rememberOverviewHistory(test);

    // Note: The respective components (FlowTesting/APITesting) should detect the selectedTest
    // and automatically load the execution history when they mount or when selectedTest changes
  };

  // Combine API tests and Flow tests whenever either changes
  useEffect(() => {
    const combinedTests = [
      ...testCases.map(test => ({ ...test, type: 'api_test' })),
      ...flowTests
    ];
    
    // Sort by creation date, newest first
    combinedTests.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
    
    setAllTests(combinedTests);
  }, [testCases, flowTests]);

  // Handle support ticket modal
  useEffect(() => {
    const handleOpenSupportTicket = () => {
      setShowSupportTicket(true);
    };

    window.addEventListener('openSupportTicket', handleOpenSupportTicket);
    
    return () => {
      window.removeEventListener('openSupportTicket', handleOpenSupportTicket);
    };
  }, []);

  // URL validation function
  const validateUrl = (url) => {
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;
    return urlPattern.test(url);
  };

  // Auto-prepend https if missing
  const normalizeUrl = (url) => {
    if (!url) return '';
    
    // If URL starts with http:// or https://, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If URL looks like a domain (contains a dot and no protocol), prepend https://
    if (url.includes('.') && !url.includes('://')) {
      return `https://${url}`;
    }
    
    return url;
  };

  // Form validation
  const validateForm = () => {
    const errors = {
      scenarioName: '',
      description: '',
      website_url: ''
    };

    // Validate scenario name
    if (!newTest.scenarioName.trim()) {
      errors.scenarioName = 'Scenario name is required';
    }

    // Validate description
    if (!newTest.description.trim()) {
      errors.description = 'Test description is required';
    }

    // Validate URL
    if (!newTest.website_url.trim()) {
      errors.website_url = 'URL is required';
    } else if (!validateUrl(newTest.website_url)) {
      errors.website_url = 'Enter a full URL including http or https';
    }

    setFormErrors(errors);
    
    // Return true if no errors
    return !Object.values(errors).some(error => error);
  };

  const generateTest = async () => {
    // Clear previous errors
    setFormErrors({ scenarioName: '', description: '', website_url: '' });
    
    // Try to auto-normalize URL first
    const normalizedUrl = normalizeUrl(newTest.website_url);
    if (normalizedUrl !== newTest.website_url) {
      setNewTest(prev => ({ ...prev, website_url: normalizedUrl }));
      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Validate form with normalized URL
    const testData = { 
      ...newTest, 
      website_url: normalizedUrl 
    };
    
    // Validate all fields
    if (!testData.scenarioName.trim() || !testData.description.trim() || !testData.website_url.trim()) {
      setFormErrors({
        scenarioName: !testData.scenarioName.trim() ? 'Scenario name is required' : '',
        description: !testData.description.trim() ? 'Test description is required' : '',
        website_url: !testData.website_url.trim() ? 'URL is required' : ''
      });
      return;
    }

    // Validate URL format
    if (!validateUrl(testData.website_url)) {
      setFormErrors({
        scenarioName: '',
        description: '',
        website_url: 'Enter a full URL including http or https'
      });
      return;
    }

    setLoading(true);
    try {
      // Convert camelCase to snake_case for backend
      const backendData = {
        scenario_name: testData.scenarioName,
        description: testData.description,
        website_url: testData.website_url
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/generate-test`, backendData);
      
      // Track API test creation
      trackAPITestCreation('ai_generated', newTest.scenarioName);
      
      await loadAllTests();
      
      // Fetch updated usage counts after test generation (read-only)
      await fetchCurrentUsageData();
      
      setNewTest({ scenarioName: '', description: '', website_url: '' });
      setFormErrors({ scenarioName: '', description: '', website_url: '' });
      setShowCreateDialog(false);
    } catch (error) {
      console.error('Failed to generate test:', error);
      if (error.response?.status === 403) {
        showError('Monthly test generation limit reached. Please upgrade your plan.');
      } else if (error.response?.status === 400) {
        // Handle validation errors from server
        let errorMsg = 'Invalid input data';
        if (error.response.data.detail) {
          const detail = error.response.data.detail;
          errorMsg = typeof detail === 'string' ? detail : 'Invalid input data';
        }
        if (errorMsg.includes('URL') || errorMsg.includes('url')) {
          setFormErrors(prev => ({ ...prev, website_url: errorMsg }));
        } else {
          showError(errorMsg);
        }
      } else {
        showError('Failed to generate test. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const executeTest = async (testId) => {
    try {
      // Update testCases state to show running status
      setTestCases(prev => prev.map(test => 
        test.id === testId ? { ...test, status: 'running' } : test
      ));

      const response = await axios.post(`${BACKEND_URL}/api/execute-test/${testId}`);
      
      // Track API test execution
      const test = allTests.find(t => t.id === testId);
      trackAPITestExecution(testId, response.data.status || 'unknown');
      
      await loadAllTests();
      
      // Fetch updated usage counts after test execution (read-only)
      await fetchCurrentUsageData();
      
    } catch (error) {
      console.error('Failed to execute test:', error);
      await loadAllTests();
      
      // Also fetch current usage data on error in case the test was partially executed
      await fetchCurrentUsageData();
    }
  };

  const deleteTest = async (testId, testName) => {
    setTeamConfirmDialog({
      show: true,
      type: 'deleteTest',
      data: { testId, testName },
      title: 'Delete Test',
      message: `Are you sure you want to delete "${testName}"?`
    });
  };

  const confirmDeleteTest = async () => {
    const { testId } = teamConfirmDialog.data;
    setTeamConfirmDialog({ show: false, type: '', data: null });
    
    try {
      await axios.delete(`${BACKEND_URL}/api/test-cases/${testId}`);
      showSuccess('Test deleted successfully');
      await loadAllTests();
    } catch (error) {
      console.error('Failed to delete test:', error);
      showError('Failed to delete test');
    }
  };

  const loadExecutions = async (testId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/test-executions/${testId}`);
      setExecutions(response.data);
      setSelectedTest(allTests.find(t => t.id === testId));
    } catch (error) {
      console.error('Failed to load executions:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PASS':
      case 'passed': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'FAIL':
      case 'failed': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'running': return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      default: return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PASS':
      case 'passed': return 'bg-green-500';
      case 'FAIL':
      case 'failed': return 'bg-red-500';
      case 'running': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const renderScreenshots = (execution) => {
    // Debug: Log execution data to understand the structure
    debugLog('Execution data for screenshots:', execution);
    
    // Check multiple possible screenshot properties
    const screenshots = execution.screenshots || execution.artifacts || execution.final_screenshot || [];
    
    // Handle different screenshot formats
    let screenshotArray = [];
    if (Array.isArray(screenshots)) {
      screenshotArray = screenshots;
    } else if (screenshots && typeof screenshots === 'string') {
      screenshotArray = [screenshots];
    } else if (screenshots && screenshots.url) {
      screenshotArray = [screenshots.url];
    }
    
    // Also check if there's a final screenshot in a different property
    if (execution.result && execution.result.artifacts) {
      if (Array.isArray(execution.result.artifacts)) {
        screenshotArray = [...screenshotArray, ...execution.result.artifacts];
      }
    }
    
    if (screenshotArray.length === 0) {
      return (
        <div className="mt-3">
          <div className="text-sm font-medium text-gray-700 mb-2">Screenshots</div>
          <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
            No screenshots available for this execution
          </div>
        </div>
      );
    }

    // Get the auth token for screenshot access
    const token = localStorage.getItem('token');

    return (
      <div className="mt-3">
        <div className="text-sm font-medium text-gray-700 mb-2">Screenshots ({screenshotArray.length})</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {screenshotArray.map((screenshot, index) => {
            // Handle different screenshot URL formats
            let screenshotUrl = '';
            if (typeof screenshot === 'string') {
              if (screenshot.startsWith('http')) {
                screenshotUrl = screenshot;
              } else {
                const filename = screenshot.split('/').pop() || screenshot;
                screenshotUrl = `${BACKEND_URL}/api/screenshot/${filename}${token ? `?token=${token}` : ''}`;
              }
            } else if (screenshot && screenshot.url) {
              screenshotUrl = screenshot.url;
            }
            
            return (
              <div key={index} className="relative group">
                <button
                  onClick={() => window.open(screenshotUrl, '_blank')}
                  className="w-full aspect-video bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 transition-colors duration-200"
                  title={`Screenshot ${index + 1} - Click to view full size`}
                >
                  <img
                    src={screenshotUrl}
                    alt={`Screenshot ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.warn('Failed to load screenshot:', screenshotUrl);
                      // Fallback to placeholder if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full flex items-center justify-center text-gray-500" style={{ display: 'none' }}>
                    <Image className="w-8 h-8" />
                    <span className="ml-2 text-sm">Failed to load</span>
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <ExternalLink className="w-6 h-6 text-white" />
                  </div>
                </button>
                <div className="text-xs text-gray-500 mt-1 truncate">
                  {screenshot.type || `Screenshot ${index + 1}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Enhanced telemetry and metadata renderer for Lexa UI Testing System
  const renderExecutionTelemetry = (execution) => {
    const hasLexaTelemetry = execution.duration_ms || execution.execution_number || execution.started_at || execution.finished_at;
    
    if (!hasLexaTelemetry) {
      return null;
    }

    return (
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center space-x-2 mb-3">
          <BarChart3 className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">Execution Telemetry</span>
          {execution.execution_number && (
            <Badge className="bg-blue-100 text-blue-800 text-xs">
              Run #{execution.execution_number}
            </Badge>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {execution.duration_ms && (
            <div className="flex items-center space-x-2">
              <Clock className="h-3 w-3 text-gray-500" />
              <span className="text-gray-600">Duration:</span>
              <span className="font-mono text-gray-800">{execution.duration_ms}ms</span>
            </div>
          )}
          
          {execution.started_at && (
            <div className="flex items-center space-x-2">
              <PlayCircle className="h-3 w-3 text-green-500" />
              <span className="text-gray-600">Started:</span>
              <span className="font-mono text-gray-800 text-xs">
                {new Date(execution.started_at).toLocaleTimeString()}
              </span>
            </div>
          )}
          
          {execution.finished_at && (
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-3 w-3 text-blue-500" />
              <span className="text-gray-600">Finished:</span>
              <span className="font-mono text-gray-800 text-xs">
                {new Date(execution.finished_at).toLocaleTimeString()}
              </span>
            </div>
          )}
          
          {execution.screenshots && execution.screenshots.length > 0 && (
            <div className="flex items-center space-x-2">
              <Image className="h-3 w-3 text-purple-500" />
              <span className="text-gray-600">Artifacts:</span>
              <span className="font-mono text-gray-800">{execution.screenshots.length}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Version banner renderer for Lexa system
  const renderVersionBanner = (execution) => {
    // Check if execution log contains version information from Lexa system
    if (execution.execution_log && execution.execution_log.includes('UI Planner v1.0')) {
      const versionMatch = execution.execution_log.match(/UI Planner v[\d.]+.*?Generated: ([\d-]+\s[\d:]+)/);
      if (versionMatch) {
        return (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Brain className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Lexa Universal Planner</span>
              <Badge className="bg-green-100 text-green-800 text-xs">v1.0</Badge>
              <span className="text-xs text-green-600">Advanced AI Test Generation</span>
            </div>
            {versionMatch[1] && (
              <div className="text-xs text-green-600 mt-1">
                Generated: {versionMatch[1]}
              </div>
            )}
          </div>
        );
      }
    }
    return null;
  };

  // Check if user has team management access
  // Determine if user has team access based on subscription plan
  const hasTeamAccess = () => {
    // Check new billing system plans first
    if (globalDetailedUsage?.plan_code) {
      // Team access for plans that include multiple team members (including Business Trial)
      return ['STARTER', 'GROWTH', 'BUSINESS', 'ENTERPRISE', 'BUSINESS_TRIAL'].includes(globalDetailedUsage.plan_code);
    }
    // Fallback to legacy plan check
    if (globalUsage?.current_plan) {
      return ['STARTER', 'GROWTH', 'BUSINESS', 'ENTERPRISE', 'BUSINESS_TRIAL', 'starter', 'pro', 'business', 'enterprise', 'business_trial'].includes(globalUsage.current_plan);
    }
    // Final fallback to user plan - include starter (maps to STARTER) and pro (maps to GROWTH)
    return ['starter', 'pro', 'business', 'enterprise', 'business_trial'].includes(user?.plan);
  };

  const userHasTeamAccess = hasTeamAccess() || user?.is_owner || user?.role === 'owner';

  const dashboardContent = (
    <>
      <SEO 
        title="Lexa.run Dashboard – AI Test Automation"
        description="Manage and run automated tests with Lexa.run. Access your projects, workflows, and reports in one place."
      />
      {/* Main Content */}
      <div className="p-8">
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          if (onTabChange) onTabChange(tab);
        }} className="space-y-6">
          {/* TabsList hidden - Navigation now via sidebar */}
          {/* <TabsList className={`grid w-full ${userHasTeamAccess ? 'grid-cols-8 lg:w-[960px]' : 'grid-cols-7 lg:w-[840px]'}`}>
            ...tabs removed, sidebar navigation is primary now...
          </TabsList> */}

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6" data-screen-id="DAS-01">
            <DashboardOverview 
              navigateToTestHistory={navigateToTestHistory}
            />
          </TabsContent>

          {/* API Testing Tab */}
          <TabsContent value="api-testing" className="space-y-6">
            <APITesting 
              user={user}
              selectedTestFromDashboard={selectedTest?.type !== 'flow_test' ? selectedTest : null}
              onTestSelected={() => setSelectedTest(null)}
            />
          </TabsContent>

          {/* Flow Testing Tab */}
          <TabsContent value="flow-testing" className="space-y-6">
            <FlowTesting 
              user={user} 
              selectedTestFromDashboard={selectedTest?.type === 'flow_test' ? selectedTest : null}
              onTestSelected={() => setSelectedTest(null)} // Clear selection after handling
            />
          </TabsContent>

          {/* Test Suites Tab */}
          <TabsContent value="test-suites" className="space-y-6">
            <TestSuites user={user} />
          </TabsContent>

          {/* Sprint QA Tab - Uses URL-based routing for projects */}
          <TabsContent value="sprint-qa" className="space-y-6">
            {/* Check URL path for project routing using currentPath state */}
            {currentPath.includes('/sprint-qa/project/') ? (
              <SprintQA key={currentPath.match(/\/sprint-qa\/project\/([^/]+)/)?.[1] || 'sprint-qa'} />
            ) : (
              <SprintQAProjects />
            )}
          </TabsContent>

          <ProjectList
            user={user}
            projects={projects}
            setEditingProject={setEditingProject}
            setProjectForm={setProjectForm}
            setShowProjectModal={setShowProjectModal}
            openEditProject={openEditProject}
            deleteProject={deleteProject}
          />

          {/* CI/CD Tab */}
          <TabsContent value="cicd" className="space-y-6">
            <CICD user={user} />
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="plans" className="space-y-6">
            <TrialEntitlementSummary user={user} />
            <SubscriptionPlans 
              globalUsage={globalUsage}
              globalDetailedUsage={globalDetailedUsage} 
              fetchCurrentUsageData={fetchCurrentUsageData}
              handleUpgradePlan={handleUpgradePlan}
            />
          </TabsContent>

          {/* Billing & Payments Tab */}
          <TabsContent value="billing" className="space-y-6">
            <TrialEntitlementSummary user={user} />
            <BillingPayments user={user} />
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6">
            <Support user={user} />
          </TabsContent>

          {/* Team Tab - For Starter, Growth, Business, Enterprise */}
          {userHasTeamAccess && (
            <TabsContent value="team" className="space-y-6">
              <TeamManagementSection user={user} teamMembers={teamMembers} teamInvites={teamInvites}
                globalDetailedUsage={globalDetailedUsage} globalUsage={globalUsage}
                inviteEmail={inviteEmail} setInviteEmail={setInviteEmail} inviteTeamMember={inviteTeamMember}
                teamLoading={teamLoading} removeTeamMember={removeTeamMember}
                resendInvite={resendInvite} cancelInvite={cancelInvite}
                onChanged={loadTeamData} />
            </TabsContent>
          )}
        </Tabs>

        <ProjectDialog
          showProjectModal={showProjectModal}
          editingProject={editingProject}
          projectForm={projectForm}
          setProjectForm={setProjectForm}
          projectLoading={projectLoading}
          teamMembers={teamMembers}
          memberSearchQuery={memberSearchQuery}
          setMemberSearchQuery={setMemberSearchQuery}
          BACKEND_URL={BACKEND_URL}
          loadProjects={loadProjects}
          setEditingProject={setEditingProject}
          showError={projectFeedback.showError}
          setShowProjectModal={setShowProjectModal}
          updateProject={updateProject}
          createProject={createProject}
        />

        {/* Team Confirmation Dialog */}
        <Dialog open={teamConfirmDialog.show} onOpenChange={(open) => !open && setTeamConfirmDialog({ show: false, type: '', data: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{teamConfirmDialog.title}</DialogTitle>
              <DialogDescription>
                {teamConfirmDialog.message}
              </DialogDescription>
            </DialogHeader>
            <div className="flex space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setTeamConfirmDialog({ show: false, type: '', data: null })}
                className="flex-1"
              >
                {teamConfirmDialog.type === 'deleteProject' ? projectFeedback.t('Cancel') : 'Cancel'}
              </Button>
              <Button
                onClick={handleTeamConfirmAction}
                className={`flex-1 ${teamConfirmDialog.type === 'remove' || teamConfirmDialog.type === 'cancel' ? 'bg-red-600 hover:bg-red-700' : ''}`}
              >
                {teamConfirmDialog.type === 'deleteProject' ? projectFeedback.t('Confirm') : 'Confirm'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stripe Payment Modals */}
        {showPaymentModal && selectedPlanForPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <StripePayment
              planCode={selectedPlanForPayment}
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
            />
          </div>
        )}

        {showOverageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <OveragePurchase
              currentPlan={globalUsage?.current_plan || globalDetailedUsage?.plan_code || 'TRIAL'}
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
            />
          </div>
        )}

        {/* User Profile Modal */}
        {showUserProfileModal && (
          <UserProfileModal
            user={user}
            onClose={() => setShowUserProfileModal(false)}
            onSuccess={(updatedUser) => {
              updateUser(updatedUser);
              setShowUserProfileModal(false);
            }}
          />
        )}

        {/* Support Ticket Modal */}
        <SupportTicket 
          isOpen={showSupportTicket}
          onClose={() => setShowSupportTicket(false)}
          user={user}
        />
      </div>
    </>
  );
  
  // If renderLayout is provided, use it to wrap the dashboard
  if (renderLayout) {
    return renderLayout({
      children: dashboardContent,
      notifications,
      unreadCount,
      handleNotificationClick,
      markAllAsRead,
      clearAllNotifications,
      openUserProfile: onOpenProfile,
      globalUsage,
      globalDetailedUsage
    });
  }
  
  // Otherwise, render dashboard with default wrapper
  return <div className="min-h-screen bg-gray-50">{dashboardContent}</div>;
};

// Landing Page Component
const LandingPage = ({ onLogin, onRegister }) => {
  const [showDemoModal, setShowDemoModal] = React.useState(false);
  
  const navigateTo = (path) => {
    window.history.pushState(null, '', path);
    // Force a page reload to ensure route change is detected
    window.location.reload();
  };

  const handlePricingClick = () => {
    navigateTo('/pricing');
  };

  const handleDocsClick = () => {
    navigateTo('/docs');
  };

  const handleContactClick = () => {
    navigateTo('/contact');
  };

  const handleAboutClick = () => {
    navigateTo('/about');
  };

  const handleRequestDemo = () => {
    setShowDemoModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO 
        title="AI-Powered Test Automation | Lexa.run"
        description="Convert plain English into automated tests in seconds. Lexa.run helps teams run UI, API, and workflow tests with AI. Start testing smarter today."
      />
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-[100]">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Logo size="lg" />

            <div className="hidden md:flex items-center space-x-6 mr-6">
              {/* Solutions Dropdown */}
              <div className="relative group z-[100]">
                <button className="text-gray-600 hover:text-gray-900 font-medium transition-colors flex items-center gap-1">
                  Solutions
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                  <div className="py-2">
                    <button 
                      onClick={() => navigateTo('/solutions/web-testing')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                    >
                      <div className="font-semibold">Web Testing</div>
                      <div className="text-xs text-gray-500">Automated browser testing</div>
                    </button>
                    <button 
                      onClick={() => navigateTo('/solutions/mobile-testing')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                    >
                      <div className="font-semibold">Mobile Testing</div>
                      <div className="text-xs text-gray-500">iOS & Android devices</div>
                    </button>
                    <button 
                      onClick={() => navigateTo('/solutions/api-testing')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                    >
                      <div className="font-semibold">API Testing</div>
                      <div className="text-xs text-gray-500">REST & GraphQL APIs</div>
                    </button>
                    <button 
                      onClick={() => navigateTo('/solutions/visual-testing')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                    >
                      <div className="font-semibold">Visual Testing</div>
                      <div className="text-xs text-gray-500">Screenshots & videos</div>
                    </button>
                    <button 
                      onClick={() => navigateTo('/solutions/cross-browser')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                    >
                      <div className="font-semibold">Cross Browser Testing</div>
                      <div className="text-xs text-gray-500">Chrome, Firefox, Safari, Edge</div>
                    </button>
                  </div>
                </div>
              </div>

              <button 
                onClick={handlePricingClick} 
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={handleDocsClick} 
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Documentation
              </button>
              <button 
                onClick={handleContactClick} 
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Contact
              </button>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
              <Button 
                variant="outline" 
                onClick={onLogin}
                className="px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-base"
              >
                Sign In
              </Button>
              <Button 
                onClick={onRegister} 
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-base"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-16">
        {/* Hero Section - Problem + Solution */}
        <div className="text-center mb-12 sm:mb-20">
          {/* Social Proof Badge */}
          <div className="inline-flex items-center bg-green-50 border border-green-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6 animate-fade-in-down">
            <div className="flex -space-x-1 mr-2 sm:mr-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full border-2 border-white animate-float"></div>
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-green-400 to-teal-400 rounded-full border-2 border-white animate-float delay-100"></div>
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-orange-400 to-red-400 rounded-full border-2 border-white animate-float delay-200"></div>
            </div>
            <span className="text-xs sm:text-sm font-medium text-green-700">
              🎉 Join 10,000+ developers already automating tests with AI
            </span>
          </div>

          {/* Problem-Focused Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4 animate-fade-in-up delay-100">
            Stop Writing Tests. 
            <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent animate-gradient">
              Start Speaking Them.
            </span>
          </h1>
          
          {/* Benefit-Rich Subheadline */}
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 mb-3 sm:mb-4 max-w-4xl mx-auto font-medium px-4 animate-fade-in-up delay-200">
            Transform weeks of manual test writing into minutes of conversation.
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8 sm:mb-10 max-w-3xl mx-auto px-4 animate-fade-in-up delay-300">
            Lexa.run's AI converts your plain English descriptions into production-ready Playwright tests. 
            <span className="font-semibold text-purple-600">Save 90% of your testing time</span> and ship faster than ever.
          </p>

          {/* Urgency + Primary CTA */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 max-w-sm sm:max-w-md mx-auto mb-6 sm:mb-8 border-2 border-purple-100 mx-4 animate-scale-in delay-400 hover:shadow-2xl transition-shadow duration-300">
            <div className="text-center mb-4 sm:mb-6">
              <div className="inline-flex items-center bg-red-50 border border-red-200 rounded-full px-2 sm:px-3 py-1 mb-3 sm:mb-4">
                <span className="text-xs sm:text-sm font-bold text-red-600">⚡ LIMITED TIME: Free Forever Plan</span>
              </div>
              <Button 
                onClick={onRegister}
                size="lg" 
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 text-base sm:text-lg md:text-xl font-bold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                <span className="hidden sm:inline-block">🚀 Start FREE - Generate 5 Tests Now</span>
                <span className="inline-block sm:hidden">🚀 Start FREE - 5 Tests</span>
              </Button>
              <p className="text-xs sm:text-sm text-gray-500 mt-3">
                ✅ No Credit Card Required • ✅ Setup in 30 Seconds • ✅ Cancel Anytime
              </p>
            </div>
          </div>

          {/* Risk Reversal */}
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-8 text-xs sm:text-sm text-gray-600">
            <div className="flex items-center">
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1" />
              <span>30-Day Money Back</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1" />
              <span>Free Migration Help</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1" />
              <span>24/7 Support</span>
            </div>
          </div>
        </div>

        {/* Problem Agitation */}
        <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-6 sm:p-12 mb-12 sm:mb-20 mx-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              Are You Still Stuck in 
              <span className="text-red-600">Testing Hell?</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Weeks of Manual Writing</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Spending endless hours writing, debugging, and maintaining complex test scripts instead of building features.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Flaky, Brittle Tests</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Tests that break constantly, require constant maintenance, and give false positives that kill productivity.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <User className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Team Bottlenecks</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Only senior developers can write tests, creating deployment delays and technical debt that never ends.
              </p>
            </div>
          </div>
        </div>

        {/* Solution - Features as Benefits */}
        <div className="mb-12 sm:mb-20 px-4">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              The Solution: AI That 
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Thinks Like You</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Stop fighting with selectors and syntax. Just describe what you want to test, 
              and watch Lexa.run create bulletproof tests in seconds.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <Card className="p-6 sm:p-8 text-center hover:shadow-2xl transition-all border-2 hover:border-purple-200">
              <div className="p-3 sm:p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center">
                <Zap className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600" />
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">90% Faster Test Creation</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                "Login with admin@test.com, click dashboard, verify revenue chart" 
                → Complete Playwright test in 30 seconds. No coding skills required.
              </p>
              <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm font-bold text-green-700">
                  💰 Save $50,000+ per year in developer time
                </p>
              </div>
            </Card>

            <Card className="p-6 sm:p-8 text-center hover:shadow-2xl transition-all border-2 hover:border-green-200 transform md:scale-105">
              <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-green-400 to-blue-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                  🔥 MOST POPULAR
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-gradient-to-r from-green-100 to-blue-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">99.9% Reliable Tests</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Self-healing selectors, smart waits, and AI-powered debugging. 
                Tests that actually work in production, every single time.
              </p>
              <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm font-bold text-green-700">
                  🎯 Eliminate flaky tests forever
                </p>
              </div>
            </Card>

            <Card className="p-6 sm:p-8 text-center hover:shadow-2xl transition-all border-2 hover:border-orange-200">
              <div className="p-3 sm:p-4 bg-gradient-to-r from-orange-100 to-red-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center">
                <BarChart3 className="h-8 w-8 sm:h-10 sm:w-10 text-orange-600" />
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">Team-Wide Adoption</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Product managers, designers, and junior developers can now create tests. 
                No more testing bottlenecks blocking your releases.
              </p>
              <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm font-bold text-green-700">
                  🚀 Ship 3x faster with confident deployments
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* Social Proof & Testimonials */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl sm:rounded-2xl p-6 sm:p-12 mb-12 sm:mb-20 mx-4">
          <div className="text-center mb-8 sm:mb-12 animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Join Successful Teams Already Using Lexa.run
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600">
              See why leading companies chose AI-powered testing
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-fade-in-up delay-100">
              <div className="flex items-center mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base">
                  SC
                </div>
                <div className="ml-3">
                  <p className="font-bold text-gray-900 text-sm sm:text-base">Sarah Chen</p>
                  <p className="text-xs sm:text-sm text-gray-600">Lead QA Engineer, TechCorp</p>
                </div>
              </div>
              <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
                "Lexa.run cut our testing time by 85%. What used to take 2 weeks now takes 2 days. 
                Our deployment confidence has never been higher."
              </p>
              <div className="flex items-center text-yellow-500">
                <span className="text-xl sm:text-2xl">⭐⭐⭐⭐⭐</span>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-fade-in-up delay-200">
              <div className="flex items-center mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-teal-400 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base">
                  MR
                </div>
                <div className="ml-3">
                  <p className="font-bold text-gray-900 text-sm sm:text-base">Mike Rodriguez</p>
                  <p className="text-xs sm:text-sm text-gray-600">VP Engineering, StartupXYZ</p>
                </div>
              </div>
              <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
                "Finally, our PMs can write tests! No more waiting for engineering bandwidth. 
                We ship features 3x faster now."
              </p>
              <div className="flex items-center text-yellow-500">
                <span className="text-xl sm:text-2xl">⭐⭐⭐⭐⭐</span>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-fade-in-up delay-300">
              <div className="flex items-center mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base">
                  EJ
                </div>
                <div className="ml-3">
                  <p className="font-bold text-gray-900 text-sm sm:text-base">Emily Johnson</p>
                  <p className="text-xs sm:text-sm text-gray-600">CTO, E-commerce Pro</p>
                </div>
              </div>
              <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
                "ROI was immediate. Saved $200K in the first year just in developer time. 
                Zero flaky tests since switching."
              </p>
              <div className="flex items-center text-yellow-500">
                <span className="text-xl sm:text-2xl">⭐⭐⭐⭐⭐</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Preview Section */}
        <div className="mb-12 sm:mb-20 mx-4">
          <div className="text-center mb-8 sm:mb-12 animate-fade-in-up">
            <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4 animate-scale-in">
              Pricing
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mb-4">
              From startups to enterprises - scale your testing without breaking the bank
            </p>
          </div>

          {/* Request Demo Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 sm:p-8 max-w-4xl mx-auto mb-8 animate-fade-in-up">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white text-center md:text-left">
                <h3 className="text-2xl font-bold mb-2">Need a Custom Solution?</h3>
                <p className="text-blue-100">
                  Schedule a personalized demo and discover how Lexa.run can transform your testing workflow
                </p>
              </div>
              <Button
                onClick={handleRequestDemo}
                className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-8 py-3 whitespace-nowrap shadow-lg hover:shadow-xl transition-all"
              >
                <Mail className="h-5 w-5 mr-2" />
                Request Demo
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6 max-w-7xl mx-auto mb-8">
            {/* Trial Plan */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 transform hover:-translate-y-2 animate-fade-in-up delay-100">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Trial</h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">$0</span>
                </div>
                <p className="text-sm text-gray-600">14-day trial</p>
              </div>
              <div className="space-y-3 mb-6">
                <div className="bg-purple-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-purple-800">5 Executions/mo</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-blue-800">1 Team Member</div>
                </div>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Flow Testing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">API Testing</span>
                </li>
              </ul>
              <Button onClick={onRegister} className="w-full" variant="outline">
                Start Free Trial
              </Button>
            </div>

            {/* Starter Plan */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 transform hover:-translate-y-2 animate-fade-in-up delay-200">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">$249</span>
                  <span className="text-gray-600 ml-1">/mo</span>
                </div>
                <p className="text-sm text-gray-600">For small teams</p>
              </div>
              <div className="space-y-3 mb-6">
                <div className="bg-purple-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-purple-800">550 Executions/mo</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-blue-800">10 Team Members</div>
                </div>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Everything in Trial</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Priority Support</span>
                </li>
              </ul>
              <Button onClick={onRegister} className="w-full">
                Get Started
              </Button>
            </div>

            {/* Growth Plan - Most Popular */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-purple-300 transform scale-105 hover:-translate-y-2 animate-fade-in-up delay-300 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1">
                  Most Popular
                </Badge>
              </div>
              <div className="text-center mb-4 mt-2">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Growth</h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">$999</span>
                  <span className="text-gray-600 ml-1">/mo</span>
                </div>
                <p className="text-sm text-gray-600">For growing teams</p>
              </div>
              <div className="space-y-3 mb-6">
                <div className="bg-purple-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-purple-800">2,500 Executions/mo</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-blue-800">15 Team Members</div>
                </div>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Everything in Starter</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Advanced Analytics</span>
                </li>
              </ul>
              <Button onClick={onRegister} className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white">
                Most Popular
              </Button>
            </div>

            {/* Business Plan */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 transform hover:-translate-y-2 animate-fade-in-up delay-400">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Business</h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">$2,500</span>
                  <span className="text-gray-600 ml-1">/mo</span>
                </div>
                <p className="text-sm text-gray-600">For large teams</p>
              </div>
              <div className="space-y-3 mb-6">
                <div className="bg-purple-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-purple-800">5,500 Executions/mo</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-blue-800">Unlimited Members</div>
                </div>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Everything in Growth</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">SSO Integration</span>
                </li>
              </ul>
              <Button onClick={onRegister} className="w-full">
                Upgrade to Business
              </Button>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-700 transform hover:-translate-y-2 animate-fade-in-up delay-500">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white mb-2">Enterprise</h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-4xl font-bold text-white">Custom</span>
                </div>
                <p className="text-sm text-gray-300">Contact sales</p>
              </div>
              <div className="space-y-3 mb-6">
                <div className="bg-purple-900 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-purple-200">50,000+ Executions</div>
                </div>
                <div className="bg-blue-900 rounded-lg p-2 text-center">
                  <div className="text-sm font-semibold text-blue-200">Unlimited Members</div>
                </div>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-200">Everything in Business</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-200">Custom SLA</span>
                </li>
              </ul>
              <Button onClick={handleContactClick} className="w-full bg-white text-gray-900 hover:bg-gray-100">
                <Crown className="h-4 w-4 mr-2" />
                Contact Sales
              </Button>
            </div>
          </div>

          {/* View Full Pricing Link */}
          <div className="text-center">
            <button 
              onClick={handlePricingClick}
              className="text-purple-600 hover:text-purple-700 font-semibold text-lg flex items-center justify-center mx-auto gap-2 hover:underline"
            >
              View Full Pricing Details & Comparison
              <ExternalLink className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Integrations Section - New Design */}
        <div className="mb-12 sm:mb-20 mx-4">
          <div className="bg-white rounded-2xl p-8 sm:p-12 max-w-6xl mx-auto shadow-xl border border-gray-100">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Text */}
              <div>
                <div className="inline-block bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-scale-in">
                  🔌 Integrations
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  Fits into your existing workflow
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  With comprehensive integrations for <span className="text-blue-600 font-semibold">CI/CD</span>, <span className="text-blue-600 font-semibold">issue tracking</span>, and <span className="text-blue-600 font-semibold">test case management</span>, Lexa fits into your current development and testing process with minimal disruption.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    Quick Setup
                  </span>
                  <span className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    Two-Way Sync
                  </span>
                  <span className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    Real-time Updates
                  </span>
                </div>
              </div>

              {/* Right Column - Integration Logos */}
              <div>
                <div className="grid grid-cols-3 gap-4">
                  {/* Row 1 */}
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/poehpjw0_image.png" alt="Azure DevOps" className="h-12 w-auto object-contain" />
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/3/32/Atlassian_Bitbucket_Logo.png" alt="Bitbucket" className="h-12 w-auto object-contain" />
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/tdtq4iim_image.png" alt="CircleCI" className="h-12 w-auto object-contain" />
                  </div>

                  {/* Row 2 */}
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/9/91/Octicons-mark-github.svg" alt="GitHub" className="h-12 w-auto object-contain" />
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/lbn2nq9z_image.png" alt="Discord" className="h-12 w-auto object-contain" />
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/GitLab_logo.svg" alt="GitLab" className="h-12 w-auto object-contain" />
                  </div>

                  {/* Row 3 */}
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/r25r80p5_image.png" alt="Jenkins" className="h-12 w-auto object-contain" />
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/hymy3wvl_image.png" alt="TeamCity" className="h-12 w-auto object-contain" />
                  </div>
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 flex items-center justify-center hover:border-purple-300 hover:shadow-lg hover:bg-white transition-all duration-300 transform hover:-translate-y-1">
                    <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/mc6i5gez_image.png" alt="Jira" className="h-12 w-auto object-contain" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA with Urgency */}
        <div className="text-center bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-12 text-white mx-4 animate-gradient animate-fade-in-up">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-yellow-400 text-gray-900 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6 font-bold text-xs sm:text-sm animate-scale-in">
              ⏰ LIMITED TIME: Free Forever Plan Available
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 animate-fade-in-up delay-100">
              Ready to 10x Your Testing Speed?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl mb-3 sm:mb-4 opacity-90 animate-fade-in-up delay-200">
              Join 10,000+ developers who stopped writing tests manually
            </p>
            <p className="text-sm sm:text-base lg:text-lg xl:text-xl mb-6 sm:mb-8 lg:mb-10 opacity-80 leading-relaxed">
              Start generating production-ready tests in the next 30 seconds. 
              No credit card required, no setup hassles.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8 lg:mb-10">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2">30s</div>
                <p className="opacity-80 text-sm sm:text-base">Setup Time</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2">5</div>
                <p className="opacity-80 text-sm sm:text-base">Free Tests</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2">0</div>
                <p className="opacity-80 text-sm sm:text-base">Credit Cards</p>
              </div>
            </div>

            <Button 
              onClick={onRegister}
              size="lg" 
              className="bg-white text-purple-600 hover:bg-gray-100 px-4 sm:px-6 md:px-8 lg:px-12 py-3 sm:py-4 lg:py-6 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold rounded-lg sm:rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all mb-4 sm:mb-6 w-full sm:w-auto animate-pulse-glow animate-scale-in delay-400"
            >
              <span className="hidden sm:inline-block">🚀 Start FREE Now - Generate Your First Test</span>
              <span className="inline-block sm:hidden">🚀 Start FREE Now</span>
            </Button>
            
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4 lg:space-x-8 text-xs sm:text-sm opacity-80">
              <div className="flex items-center justify-center">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span>Free Forever Plan</span>
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span>No Credit Card</span>
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span>Instant Access</span>
              </div>
            </div>
            
            <p className="text-xs sm:text-sm mt-4 sm:mt-6 opacity-70 px-4">
              🔒 Join companies like TechCorp, StartupXYZ, and DevTeam Inc who trust Lexa.run
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <LandingFooter navigateTo={navigateTo} />
      
      {/* Demo Request Modal */}
      <DemoRequestModal 
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)} 
      />
    </div>
  );
};

// Main App Component
function App() {
  const { user, loading: authLoading, logout, updateUser } = useAuth();
  const { workspace, isMainApp, subdomain } = useWorkspace();
  
  // Initialize currentPage based on URL
  const getInitialPage = () => {
    const path = window.location.pathname;
    if (path === '/ops') {
      return 'admin';
    }
    if (path === '/get-started') {
      return 'get-started';
    }
    if (path === '/book-demo') {
      return 'book-demo';
    }
    if (path === '/talk-to-human') {
      return 'talk-to-human';
    }
    return 'landing';
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [showSupportTicket, setShowSupportTicket] = useState(false);

  // Handle URL-based navigation and route changes
  useEffect(() => {
    const handleRouteChange = () => {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      const viewParam = params.get('view');
      
      if (path === '/ops') {
        setCurrentPage('admin');
      } else if (path.startsWith('/sprint-qa')) {
        // Sprint QA routes - keep user on dashboard, the Sprint QA component handles the routing
        if (user) {
          setCurrentPage('dashboard');
          // If there's a view parameter in the URL, update the URL to use it properly
          // This ensures /sprint-qa?view=api-testing redirects to the correct view
          if (viewParam && viewParam !== 'sprint-qa') {
            // Keep the view parameter for the dashboard to pick up
            window.history.replaceState({ view: viewParam }, '', `/sprint-qa?view=${viewParam}`);
          }
        }
      } else if (path === '/testing') {
        // Direct /testing route should show API Testing view
        if (user) {
          setCurrentPage('dashboard');
          params.set('view', 'api-testing');
          window.history.replaceState({ view: 'api-testing' }, '', `/testing?${params}`);
        }
      } else if (path === '/') {
        // Only reset to landing if we're not already logged in
        if (!user) {
          setCurrentPage('landing');
        } else {
          setCurrentPage('dashboard');
        }
      }
    };

    // Handle initial load
    handleRouteChange();

    // Handle browser back/forward buttons
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [user]);

  // Handle URL-based navigation
  useEffect(() => {
    const path = window.location.pathname;
    
    if (path === '/ops' || path === '/ops/') {
      setCurrentPage('ops');
    } else if (path === '/docs' || path === '/documentation') {
      setCurrentPage('documentation');
    } else if (path === '/pricing') {
      setCurrentPage('pricing');
    } else if (path === '/partners') {
      setCurrentPage('partners');
    } else if (path === '/contact') {
      setCurrentPage('contact');
    } else if (path === '/about') {
      setCurrentPage('about');
    }
    
    // Listen for URL changes (back/forward buttons)
    const handlePopState = () => {
      const newPath = window.location.pathname;
      if (newPath === '/ops' || newPath === '/ops/') {
        setCurrentPage('ops');
      } else if (newPath === '/docs' || newPath === '/documentation') {
        setCurrentPage('documentation');
      } else if (newPath === '/pricing') {
        setCurrentPage('pricing');
      } else if (newPath === '/partners') {
        setCurrentPage('partners');
      } else if (newPath === '/solutions' || newPath === '/solutions/') {
        setCurrentPage('solutions');
      } else if (newPath === '/solutions/web-testing') {
        setCurrentPage('web-testing');
      } else if (newPath === '/solutions/mobile-testing') {
        setCurrentPage('mobile-testing');
      } else if (newPath === '/solutions/api-testing') {
        setCurrentPage('api-testing');
      } else if (newPath === '/solutions/visual-testing') {
        setCurrentPage('visual-testing');
      } else if (newPath === '/solutions/cross-browser') {
        setCurrentPage('cross-browser');
      } else if (newPath === '/about') {
        setCurrentPage('about');
      } else if (newPath === '/status') {
        setCurrentPage('status');
      } else if (newPath === '/terms') {
        setCurrentPage('terms');
      } else if (newPath === '/privacy') {
        setCurrentPage('privacy');
      } else if (newPath === '/security') {
        // Security page disabled - redirect to privacy
        window.history.replaceState(null, '', '/privacy');
        setCurrentPage('privacy');
      } else if (newPath === '/features') {
        setCurrentPage('features');
      } else if (newPath === '/changelog') {
        setCurrentPage('changelog');
      } else if (newPath === '/roadmap') {
        // Roadmap page disabled - redirect to changelog
        window.history.replaceState(null, '', '/changelog');
        setCurrentPage('changelog');
      } else if (newPath === '/guides') {
        setCurrentPage('guides');
      } else if (newPath === '/developer') {
        setCurrentPage('api-reference');
      } else if (newPath === '/careers') {
        setCurrentPage('careers');
      } else if (newPath === '/press') {
        setCurrentPage('press');
      } else if (newPath === '/blog' || newPath.startsWith('/blog/')) {
        setCurrentPage('blog');
      } else if (newPath === '/contact') {
        setCurrentPage('contact');
      } else if (newPath === '/get-started') {
        setCurrentPage('get-started');
      } else if (newPath === '/book-demo') {
        setCurrentPage('book-demo');
      } else if (newPath === '/talk-to-human') {
        setCurrentPage('talk-to-human');
      } else if (newPath === '/') {
        setCurrentPage(user ? 'dashboard' : 'landing');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user]);

  // Handle URL-based navigation
  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    
    // Invitation onboarding owns this route without changing the current session.
    if (path.startsWith('/team/accept-invitation/')) return;
    // Funnel hand-off: /?signup=1 (with optional email/name/app_url/source) opens
    // the register screen with the lead's data pre-filled. Honors funnel-captured leads.
    if (path === '/' && (urlParams.get('signup') === '1' || urlParams.get('signup') === 'true')) {
      // Stash funnel context for the register form to consume
      try {
        const ctx = signupContext(urlParams);
        sessionStorage.setItem('funnelSignupContext', JSON.stringify(ctx));
      } catch (_) { /* ignore */ }
      if (!user) setCurrentPage('register');
    }
    // Funnel hand-off: /?login=1 (e.g., when the funnel detected an already-registered email)
    else if (path === '/' && (urlParams.get('login') === '1' || urlParams.get('login') === 'true')) {
      try {
        const ctx = { email: urlParams.get('email') || '' };
        sessionStorage.setItem('funnelLoginContext', JSON.stringify(ctx));
      } catch (_) { /* ignore */ }
      if (!user) setCurrentPage('login');
    }
    // Check if this is a password reset URL
    else if (path === '/reset-password' && urlParams.get('token')) {
      setCurrentPage('reset-password');
    }
    // Handle OPS dashboard access
    else if (path === '/ops') {
      setCurrentPage('ops');
    }
    // Handle pricing page
    else if (path === '/pricing') {
      setCurrentPage('pricing');
    }
    // Handle solutions index page
    else if (path === '/solutions' || path === '/solutions/') {
      setCurrentPage('solutions');
    }
    // Handle solution pages
    else if (path === '/solutions/web-testing') {
      setCurrentPage('web-testing');
    }
    else if (path === '/solutions/mobile-testing') {
      setCurrentPage('mobile-testing');
    }
    else if (path === '/solutions/api-testing') {
      setCurrentPage('api-testing');
    }
    else if (path === '/solutions/visual-testing') {
      setCurrentPage('visual-testing');
    }
    else if (path === '/solutions/cross-browser') {
      setCurrentPage('cross-browser');
    }
    // Handle about page
    else if (path === '/about') {
      setCurrentPage('about');
    }
    // Handle status page
    else if (path === '/status') {
      setCurrentPage('status');
    }
    // Handle terms page
    else if (path === '/terms') {
      setCurrentPage('terms');
    }
    // Handle privacy page
    else if (path === '/privacy') {
      setCurrentPage('privacy');
    }
    // Handle security page - DISABLED, redirect to privacy
    else if (path === '/security') {
      window.history.replaceState(null, '', '/privacy');
      setCurrentPage('privacy');
    }
    // Handle features page
    else if (path === '/features') {
      setCurrentPage('features');
    }
    // Handle changelog page
    else if (path === '/changelog') {
      setCurrentPage('changelog');
    }
    // Handle roadmap page - DISABLED, redirect to changelog
    else if (path === '/roadmap') {
      window.history.replaceState(null, '', '/changelog');
      setCurrentPage('changelog');
    }
    // Handle guides page
    else if (path === '/guides') {
      setCurrentPage('guides');
    }
    // Handle API reference page
    else if (path === '/developer') {
      setCurrentPage('api-reference');
    }
    // Handle careers page
    else if (path === '/careers') {
      setCurrentPage('careers');
    }
    // Handle press page
    else if (path === '/press') {
      setCurrentPage('press');
    }
    // Handle blog page
    else if (path === '/blog' || path.startsWith('/blog/')) {
      setCurrentPage('blog');
    }
    // Handle contact page
    else if (path === '/contact') {
      setCurrentPage('contact');
    }
    // Handle marketing funnel pages (ads landing pages — direct link only)
    else if (path === '/get-started') {
      setCurrentPage('get-started');
    }
    else if (path === '/book-demo') {
      setCurrentPage('book-demo');
    }
    else if (path === '/talk-to-human') {
      setCurrentPage('talk-to-human');
    }
    // Handle documentation page - DISABLED, redirect to guides
    else if (path === '/docs' || path === '/documentation') {
      window.history.replaceState(null, '', '/guides');
      setCurrentPage('guides');
    }
  }, []);

  // Determine what to show based on auth state
  useEffect(() => {
    if (user && currentPage !== 'dashboard') {
      setCurrentPage('dashboard');
    } else if (!user && currentPage === 'dashboard') {
      setCurrentPage('landing');
    }
  }, [user, currentPage]);

  // Handle support ticket modal
  useEffect(() => {
    const handleOpenSupportTicket = () => {
      setShowSupportTicket(true);
    };

    window.addEventListener('openSupportTicket', handleOpenSupportTicket);
    
    return () => {
      window.removeEventListener('openSupportTicket', handleOpenSupportTicket);
    };
  }, []);

  const invitationPath = window.location.pathname.match(/^\/team\/accept-invitation\/([^/]+)\/?$/);
  if (invitationPath) {
    return <InvitationOnboarding inviteId={invitationPath[1]} token={new URLSearchParams(window.location.search).get('token')} />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Zap className="h-16 w-16 text-purple-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading Lexa.run...</p>
        </div>
      </div>
    );
  }

  // Show different pages based on current state
  switch (currentPage) {
    case 'login':
      return (
        <LoginPage 
          onBack={() => setCurrentPage('landing')}
          onSwitchToRegister={() => setCurrentPage('register')}
          onForgotPassword={() => setCurrentPage('forgot-password')}
        />
      );
    
    case 'register':
      return (
        <RegisterPage 
          onBack={() => setCurrentPage('landing')}
          onSwitchToLogin={() => setCurrentPage('login')}
        />
      );
    
    case 'forgot-password':
      return (
        <ForgotPasswordPage 
          onBack={() => setCurrentPage('login')}
        />
      );
    
    case 'reset-password':
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      return (
        <ResetPasswordPage 
          token={token}
          onBack={() => setCurrentPage('login')}
        />
      );
    
    case 'dashboard':
      // Only render Dashboard if user exists
      if (!user) return null;
      
      // Wrapper component to manage tab state between sidebar and dashboard
      const DashboardWithSidebar = () => {
        // Initialize activeView from URL parameter or path
        const getInitialView = () => {
          const path = window.location.pathname;
          const params = new URLSearchParams(window.location.search);
          const viewParam = params.get('view');
          
          // View parameter takes highest priority - always use it if present
          if (viewParam) {
            return viewParam;
          }
          
          // Map paths to views for direct URL access (only when no view param)
          if (path === '/testing' || path.includes('/testing')) {
            return 'api-testing';
          }
          if (path.startsWith('/sprint-qa')) {
            return 'sprint-qa';
          }
          
          return 'dashboard';
        };
        
        const [activeView, setActiveView] = React.useState(getInitialView);
        const [previousView, setPreviousView] = React.useState('dashboard');
        const dashboardRef = React.useRef(null);
        
        // Re-sync view from URL on mount (handles direct URL navigation)
        React.useEffect(() => {
          const currentView = getInitialView();
          if (currentView !== activeView) {
            setActiveView(currentView);
          }
        }, []); // Run once on mount
        
        // Update URL when view changes
        const handleViewChange = (view) => {
          // Store current view as previous view ONLY when going TO settings
          if (view === 'settings') {
            setPreviousView(activeView);
          }
          setActiveView(view);
          
          // Update URL with correct path based on view
          let basePath = '/';
          if (view === 'sprint-qa') {
            basePath = '/sprint-qa';
          } else if (view === 'api-testing') {
            basePath = '/testing';
          }
          
          const newUrl = `${basePath}?view=${view}`;
          window.history.pushState({ view }, '', newUrl);
        };
        
        // Listen for browser back/forward button
        React.useEffect(() => {
          const handlePopState = (event) => {
            if (event.state && event.state.view) {
              setActiveView(event.state.view);
            } else {
              const params = new URLSearchParams(window.location.search);
              setActiveView(params.get('view') || 'dashboard');
            }
          };
          
          window.addEventListener('popstate', handlePopState);
          return () => window.removeEventListener('popstate', handlePopState);
        }, []);
        
        // Show settings page if active view is settings
        if (activeView === 'settings') {
          return (
            <UserSettings
              user={user}
              onUpdateProfile={async (data) => {
                try {
                  const response = await axios.put(`${BACKEND_URL}/api/auth/profile`, {
                    name: data.name,
                    email: data.email,
                    phone: data.phone
                  });
                  
                  // Update user state with new data (response.data is the user object directly)
                  updateUser(response.data);
                } catch (error) {
                  console.error('Profile update failed:', error);
                  const errorMsg = error.response?.data?.detail || 'Failed to update profile';
                  throw new Error(errorMsg);
                }
              }}
              onChangePassword={async (currentPassword, newPassword, confirmPassword) => {
                try {
                  await axios.put(`${BACKEND_URL}/api/auth/change-password`, {
                    current_password: currentPassword,
                    new_password: newPassword,
                    confirm_password: confirmPassword
                  });
                  
                } catch (error) {
                  console.error('Password change failed:', error);
                  const errorMsg = error.response?.data?.detail || 'Failed to change password';
                  throw new Error(errorMsg);
                }
              }}
              onBack={() => {
                console.log('Settings onBack called, returning to:', previousView);
                handleViewChange(previousView);
              }}
            />
          );
        }

        // We'll use a render prop pattern to access Dashboard's internal state
        return (
          <Dashboard 
            initialView={activeView}
            onTabChange={handleViewChange}
            onOpenProfile={() => handleViewChange('settings')}
            renderLayout={(dashboardProps) => (
              <UserSidebarLayout 
                user={user} 
                onLogout={logout}
                activeView={activeView}
                onViewChange={handleViewChange}
                notifications={dashboardProps.notifications || []}
                unreadCount={dashboardProps.unreadCount || 0}
                onNotificationClick={dashboardProps.handleNotificationClick}
                onMarkAllRead={dashboardProps.markAllAsRead}
                onClearAll={dashboardProps.clearAllNotifications}
                onOpenProfile={dashboardProps.openUserProfile}
                planDisplayName={dashboardProps.globalDetailedUsage?.plan_display_name || dashboardProps.globalUsage?.plan_display_name}
              >
                {dashboardProps.children}
              </UserSidebarLayout>
            )}
          />
        );
      };
      
      return <DashboardWithSidebar />;
    
    case 'documentation':
      // Documentation page disabled - redirect to guides
      window.history.replaceState(null, '', '/guides');
      return (
        <Guides 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );
    
    default:
      return (
        <LandingPageV2 
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'get-started':
      return <GetStartedFunnel />;

    case 'book-demo':
      return <BookDemoFunnel />;

    case 'talk-to-human':
      return <TalkToHumanFunnel />;
    
    case 'pricing':
      return (
        <Pricing 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'partners':
      return (
        <PartnerProgram 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'contact':
      return (
        <Contact 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'about':
      return (
        <About 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'status':
      return (
        <Status 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'terms':
      return (
        <Terms 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'privacy':
      return (
        <Privacy 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'security':
      // Security page disabled - redirect to privacy
      window.history.replaceState(null, '', '/privacy');
      return (
        <Privacy 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'features':
      return (
        <Features 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'changelog':
      return (
        <Changelog 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'roadmap':
      // Roadmap page disabled - redirect to changelog
      window.history.replaceState(null, '', '/changelog');
      return (
        <Changelog 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'guides':
      return (
        <Guides 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'api-reference':
      return (
        <APIReference 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'careers':
      return (
        <Careers 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'press':
      return (
        <Press 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    case 'blog':
      // Extract slug from URL if present (e.g., /blog/my-article-slug)
      const blogPath = window.location.pathname;
      const blogSlug = blogPath.startsWith('/blog/') ? blogPath.replace('/blog/', '') : null;
      
      return (
        <Blog 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          initialSlug={blogSlug}
        />
      );

    // Solutions index page
    case 'solutions':
      return (
        <SolutionsIndex
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onLogin={() => setCurrentPage('login')}
          onRegister={() => setCurrentPage('register')}
          navigateTo={(path) => {
            window.history.pushState(null, '', path);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      );

    // Solution pages
    case 'web-testing':
      return (
        <WebTesting
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'mobile-testing':
      return (
        <MobileTesting
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'api-testing':
      return (
        <APITestingSolution
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'visual-testing':
      return (
        <VisualTesting
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'cross-browser':
      return (
        <CrossBrowserTesting
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          onRegister={() => setCurrentPage('register')}
        />
      );

    case 'admin':
    case 'ops':
      // Update URL to /ops when accessing OPS dashboard
      if (window.location.pathname !== '/ops') {
        window.history.pushState(null, '', '/ops');
      }
      return (
        <OPSDashboard 
          onBack={() => {
            window.history.pushState(null, '', '/');
            setCurrentPage(user ? 'dashboard' : 'landing');
          }}
          user={user}
        />
      );
  }
}

// Workspace-aware App wrapper
function AppWithWorkspace() {
  const { isLoading, error, subdomain, isMainApp, workspace } = useWorkspace();
  
  // Show loading state while detecting workspace
  if (isLoading) {
    return <WorkspaceLoading />;
  }
  
  // Handle workspace errors
  if (error === 'workspace_not_found') {
    return <WorkspaceNotFound subdomain={subdomain} />;
  }
  
  if (error === 'workspace_suspended') {
    return <WorkspaceSuspended workspace={workspace} />;
  }
  
  // Render the main app (works for both main app and workspace contexts)
  // The App component will have access to workspace context via useWorkspace()
  return <App />;
}

// Main App with all providers
function AppWithAuth() {
  return (
    <LanguageProvider>
      <WorkspaceProvider>
        <AuthProvider>
          <OrganizationAccessNotice><AppWithWorkspace /></OrganizationAccessNotice>
          <Toaster />
        </AuthProvider>
      </WorkspaceProvider>
    </LanguageProvider>
  );
}

export default AppWithAuth;
